import React from 'react';
import { ChromePicker } from 'react-color';
import './BannerEditor.css';
import { useCanvasTransform } from '../../hooks/useCanvasTransform';
import { useWheelZoom } from '../../hooks/useWheelZoom';
import { usePan } from '../../hooks/usePan';
import { CanvasView } from '../CanvasView';
import { useObjects } from '../../hooks/useObjects';
import { useRectDraw } from '../../hooks/useRectDraw';
import { renderArtboards } from '../../renderer/ArtboardsRenderer';
import { renderObjects, renderTempRect, clearAllCaches } from '../../renderer/ObjectsRenderer';
import { useSelect } from '../../hooks/useSelect';
import { renderOverlay } from '../../renderer/OverlayRenderer';
import { computeAABB, computeRotatedAABB, type AABB } from '../../utils/aabb';
import { getRotatedAABB } from '../../utils/alignUtils';
import { exportArtboardAsImage } from '../../utils/exportUtils';
import { useMoveSelected } from '../../hooks/useMoveSelected';
import { useResizeSelected } from '../../hooks/useResizeSelected';
import { useRotateSelected } from '../../hooks/useRotateSelected';
import { useImageRegistry } from '../../hooks/useImageRegistry';
import { useImageDragDrop } from '../../hooks/useImageDragDrop';
import { hitTestImageAtPoint } from '../../utils/hitTestImage';
import { computeFileHash } from '../../utils/imageHash';
import { useClipMask } from '../../hooks/useClipMask';
import { useClipMaskRelations } from '../../hooks/useClipMaskRelations';
import { useKeyboard } from '../../hooks/useKeyboard';
import { useHistory } from '../../hooks/useHistory';
import { useClipboard } from '../../hooks/useClipboard';
import { useFontManager } from '../../hooks/useFontManager';
import type { CanvasObject, UsedColor } from '../../types';
import JSZip from 'jszip';
import { getBestArtboardForObject, getClickedArtboard } from '../../utils/artboardUtils';
import type { Artboard } from '../../hooks/useCanvasTransform';
import { AlignToolbar } from './AlignToolbar';
import { OpacityControl } from './LeftSidebar/OpacityControl';
import { BlendModeControl } from './LeftSidebar/BlendModeControl';
import { LayerList } from './LeftSidebar/LayerList';
import { HelpModal } from './HelpModal';
import { TemplateGallery } from './TemplateGallery';

export const BannerEditor: React.FC = () => {
  const canvasRef = React.useRef<HTMLCanvasElement | null>(null);
  const [activeTool, setActiveTool] = React.useState<'select' | 'rectangle' | 'circle' | 'text' | 'artboard'>('select');
  
  // 아트보드 선택 상태
  const [selectedArtboardId, setSelectedArtboardId] = React.useState<string | null>(null);
  
  // 폰트 관리
  const { fontFamilies, isLoading: fontsLoading } = useFontManager();
  
  // 선택된 폰트 패밀리의 두께 목록 가져오기
  const getAvailableWeights = React.useCallback((fontFamily: string | undefined) => {
    if (!fontFamily) return [];
    const family = fontFamilies.find(f => f.family === fontFamily);
    return family?.weights || [];
  }, [fontFamilies]);
  
  // 언어별 아이콘 매핑
  const getLanguageIcon = React.useCallback((language: string | undefined) => {
    const iconMap: Record<string, string> = {
      '한국어': '🇰🇷',
      '영어': '🇺🇸',
      '일본어': '🇯🇵',
      '중국어': '🇨🇳',
      '기타': '🌐'
    };
    return iconMap[language || '기타'] || '🌐';
  }, []);
  
  // 폰트를 언어별로 그룹화
  const groupedFontsByLanguage = React.useMemo(() => {
    const groups: Record<string, typeof fontFamilies> = {};
    
    fontFamilies.forEach(family => {
      const language = family.language || '기타';
      if (!groups[language]) {
        groups[language] = [];
      }
      groups[language].push(family);
    });
    
    // 언어 순서 정의 (일본어, 영어, 한국어, 중국어, 기타)
    const languageOrder = ['일본어', '영어', '한국어', '중국어', '기타'];
    const sortedGroups: Array<{ language: string; families: typeof fontFamilies }> = [];
    
    // 정의된 순서대로 추가
    languageOrder.forEach(lang => {
      if (groups[lang]) {
        sortedGroups.push({ language: lang, families: groups[lang] });
      }
    });
    
    // 정의되지 않은 언어들 추가
    Object.keys(groups).forEach(lang => {
      if (!languageOrder.includes(lang)) {
        sortedGroups.push({ language: lang, families: groups[lang] });
      }
    });
    
    return sortedGroups;
  }, [fontFamilies]);
  
  // 텍스트 입력 모드 상태
  const [textInputMode, setTextInputMode] = React.useState<{ x: number; y: number; editingId?: string } | null>(null);
  const [textInputValue, setTextInputValue] = React.useState<string>('');
  const textInputRef = React.useRef<HTMLTextAreaElement>(null);
  
  // 정보 섹션 입력값 로컬 상태 (성능 최적화: onBlur에서만 업데이트)
  const [inputValues, setInputValues] = React.useState({
    x: '',
    y: '',
    width: '',
    height: '',
    rotation: '',
    borderRadiusTopLeft: '',
    borderRadiusTopRight: '',
    borderRadiusBottomLeft: '',
    borderRadiusBottomRight: ''
  });
  
  // 입력 필드 포커스 상태 추적 (입력 중 덮어쓰기 방지)
  const focusedInputRef = React.useRef<string | null>(null);
  
  // 이전 selectedObjectValues 추적 (불필요한 업데이트 방지)
  const prevSelectedObjectValuesRef = React.useRef<{
    x: number;
    y: number;
    width: number;
    height: number;
    rotation: number;
    borderRadiusTopLeft: number;
    borderRadiusTopRight: number;
    borderRadiusBottomLeft: number;
    borderRadiusBottomRight: number;
  } | null>(null);
  
  // 둥근 모서리 연결 여부
  const [borderRadiusLinked, setBorderRadiusLinked] = React.useState(true);
  
  // 테두리 효과 섹션 토글 상태
  const [isBorderEffectsOpen, setIsBorderEffectsOpen] = React.useState(false);
  
  // 테두리 두께 임시 입력값 (onBlur 전까지)
  const [tempBorderWidth, setTempBorderWidth] = React.useState('');
  
  // 아웃글로우 효과 섹션 토글 상태
  const [isOutGlowOpen, setIsOutGlowOpen] = React.useState(false);
  
  // 블러 효과 섹션 토글 상태
  const [isBlurOpen, setIsBlurOpen] = React.useState(false);
  
  // 아웃글로우 임시 입력값 (onBlur 전까지)
  const [tempOutGlowOpacity, setTempOutGlowOpacity] = React.useState('');
  const [tempOutGlowSize, setTempOutGlowSize] = React.useState('');
  const [tempOutGlowSpread, setTempOutGlowSpread] = React.useState('');
  
  // 블러 임시 입력값 (onBlur 전까지)
  const [tempBlurRadius, setTempBlurRadius] = React.useState('');
  
  // 폰트 크기 임시 입력값 (onBlur 전까지)
  const [tempFontSize, setTempFontSize] = React.useState('');
  
  // 회전 모드 (그룹 회전 / 개별 회전)
  const [rotateAsGroup, setRotateAsGroup] = React.useState(false);

  // 기본 색상 (선택된 객체가 없을 때 변경한 색상)
  const [defaultColor, setDefaultColor] = React.useState('#007bff');

  // 편집 중인 색상 정보 (사용된 색상 스와치 클릭 시)
  const [editingColor, setEditingColor] = React.useState<{
    id: string;
    type: UsedColor['type'];
    color: string;
    originalColor: string; // 원래 색상 값 (색상 변경 시 참조용)
  } | null>(null);

  // 사용된 색상 섹션 ref (외부 클릭 감지용)
  const usedColorsSectionRef = React.useRef<HTMLDivElement>(null);

  // 사용된 색상 섹션 외부 클릭 시 선택 해제
  React.useEffect(() => {
    if (!editingColor) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      
      // 사용된 색상 섹션 내부 클릭은 무시
      if (usedColorsSectionRef.current?.contains(target)) {
        return;
      }
      
      // 컬러피커 내부 클릭도 무시
      const colorPicker = (event.target as HTMLElement).closest('.chrome-picker');
      if (colorPicker) {
        return;
      }
      
      // 외부 클릭 시 선택 해제
      setEditingColor(null);
    };

    document.addEventListener('mousedown', handleClickOutside);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [editingColor]);

  // 텍스트 교체 기능 상태
  const [isTextReplaceOpen, setIsTextReplaceOpen] = React.useState<boolean>(false);
  const [textReplaceMap, setTextReplaceMap] = React.useState<Record<string, string>>({});
  
  // 도움말 모달 상태
  const [isHelpModalOpen, setIsHelpModalOpen] = React.useState<boolean>(false);
  const [isTemplateGalleryOpen, setIsTemplateGalleryOpen] = React.useState<boolean>(false);
  
  // 프로젝트 이름
  const [projectName, setProjectName] = React.useState('');
  // 불러온 템플릿 이름 (ZIP 파일명)
  const [loadedTemplateName, setLoadedTemplateName] = React.useState<string>('');
  const [layerFilterResetKey, setLayerFilterResetKey] = React.useState(0);
  
  // 기본 아트보드 배열 (새 프로젝트 시 초기화용)
  const defaultArtboards: Artboard[] = [
    { id: 'mobile', x: -2000, y: -1300, width: 1200, height: 1920, name: '1200x1920', color: '#FFFFFF' },
    { id: 'tablet', x: 100, y: -1300, width: 1200, height: 1500, name: '1200x1500', color: '#FFFFFF' },
    { id: 'square', x: -2000, y: 1300, width: 1200, height: 1200, name: '1200x1200', color: '#FFFFFF' },
    { id: 'banner', x: 100, y: 1300, width: 1200, height: 628, name: '1200x628', color: '#FFFFFF' }
  ];

  // 아트보드 상태 관리
  const [artboards, setArtboards] = React.useState<Artboard[]>(defaultArtboards);
  
  // 초기 아트보드(렌더링용 - artboards와 동일)
  // artboards state가 변경되면 자동으로 반영됨
  const initialArtboards = React.useMemo(() => artboards, [artboards]);

  const { canvasState, setCanvasState, setCustomDraw, requestRender, setGetIsPanning } = useCanvasTransform(canvasRef, artboards);
  useWheelZoom(canvasRef, setCanvasState);
  const { getIsPanning } = usePan(canvasRef, setCanvasState) as unknown as { getIsPanning: () => boolean };
  
  // getIsPanning을 useCanvasTransform에 전달
  React.useEffect(() => {
    setGetIsPanning(getIsPanning);
  }, [getIsPanning, setGetIsPanning]);

  // 객체/드로잉
  const { objects, zOrder, setZOrder, setObjects, getObjectsArray, getObjectById, tempRectRef, beginRect, updateRect, commitRect, beginCircle, updateCircle, commitCircle, clearTemp, selectedId, setSelectedId, selectedIds, setSelectedIds, addToSelection, removeFromSelection, setSelection, clearSelection, getSelectedObjects, hitTestPoint, moveSelectedBy, resizeSelected, rotateSelectedBy, updateObject, addObject, deleteObject, moveObjectsInZOrder, moveSelectedInZOrderByShortcut, restoreState } = useObjects(defaultColor);

  // 히스토리 관리
  const { saveToHistory, undo, redo, canUndo, canRedo } = useHistory();

  // 프로젝트 저장 함수 (ZIP 파일로 저장)
  const handleSaveProject = React.useCallback(async () => {
    try {
      const timestamp = new Date().getTime();
      const projectData = {
        objects: Array.from(objects.values()),
        zOrder: [...zOrder],
        artboards: initialArtboards,
        selectedId: selectedId,
        metadata: {
          version: '1.0.0',
          savedAt: new Date().toISOString()
        }
      };

      // ZIP 파일 생성
      const zip = new JSZip();
      
      // JSON 파일 추가
      const jsonData = JSON.stringify(projectData, null, 2);
      zip.file('project.json', jsonData);

      // 이미지 객체 찾기 및 ZIP에 추가
      const imageObjects = Array.from(objects.values()).filter(obj => obj.type === 'image' && obj.imageSrc);
      
      // 중복 제거: imageHash 또는 imageSrc로 유일화
      const uniqueImages = new Map<string, CanvasObject>();
      for (const obj of imageObjects) {
        const key = (obj as any).imageHash || obj.imageSrc!;
        if (!uniqueImages.has(key)) {
          uniqueImages.set(key, obj);
        }
      }

      // 이미지 파일들을 ZIP에 추가하고 객체에 인덱스 정보 저장
      if (uniqueImages.size > 0) {
        const imageArray = Array.from(uniqueImages.values());
        const imageIndexMap = new Map<string, number>(); // imageHash/imageSrc -> index
        
        for (let idx = 0; idx < imageArray.length; idx++) {
          const obj = imageArray[idx];
          const key = (obj as any).imageHash || obj.imageSrc!;
          imageIndexMap.set(key, idx);
          
          try {
            const response = await fetch(obj.imageSrc!);
            const imageBlob = await response.blob();
            
            // 이미지 확장자 추출 (기본값: png)
            const extension = obj.imageSrc!.match(/\.(jpg|jpeg|png|gif|webp)$/i)?.[1] || 'png';
            zip.file(`images/image_${idx}.${extension}`, imageBlob);
          } catch (error) {
            console.error('이미지 저장 실패:', obj.id, error);
          }
        }
        
        // 프로젝트 데이터의 이미지 객체에 인덱스 정보 추가
        projectData.objects = projectData.objects.map((obj: any) => {
          if (obj.type === 'image' && obj.imageSrc) {
            const key = obj.imageHash || obj.imageSrc;
            const imageIndex = imageIndexMap.get(key);
            if (imageIndex !== undefined) {
              return { ...obj, _imageIndex: imageIndex };
            }
          }
          return obj;
        });
        
        // 업데이트된 프로젝트 데이터로 JSON 파일 재생성
        const updatedJsonData = JSON.stringify(projectData, null, 2);
        zip.remove('project.json');
        zip.file('project.json', updatedJsonData);
      }

      // ZIP 파일 생성 및 다운로드 (Windows 호환성 향상을 위한 옵션 설정)
      const zipBlob = await zip.generateAsync({
        type: 'blob',
        compression: 'DEFLATE',
        compressionOptions: { level: 7 },  // 압축 레벨 7 (파일 크기와 성능의 균형)
        platform: 'DOS',  // Windows/DOS 형식으로 생성 (Mac에서도 정상 동작)
        mimeType: 'application/zip'
      });
      
      // 파일명 생성: 프로젝트 이름이 있으면 프로젝트이름.zip, 없으면 template_0000.zip
      let fileName: string;
      if (projectName && projectName.trim()) {
        // 프로젝트 이름에서 파일명에 사용할 수 없는 문자 제거
        const safeProjectName = projectName.trim().replace(/[<>:"/\\|?*]/g, '_').replace(/\s+/g, '_');
        fileName = `${safeProjectName}.zip`;
      } else {
        fileName = `template_0000.zip`;
      }

      
      // 새 프로젝트 저장 시 템플릿 이름 초기화
      setLoadedTemplateName('');
      
      // File System Access API 사용 (지원되는 경우)
      if ('showSaveFilePicker' in window) {
        try {
          const fileHandle = await (window as any).showSaveFilePicker({
            id: 'template-save',  // 마지막 저장 위치를 기억하기 위한 고유 ID
            suggestedName: fileName,
            startIn: 'downloads',  // 다운로드 폴더를 기본 위치로 설정
            types: [{
              description: 'ZIP Archive',
              accept: { 'application/zip': ['.zip'] }
            }]
          });
          
          const writable = await fileHandle.createWritable();
          await writable.write(zipBlob);
          await writable.close();
          
          return; // 성공적으로 저장했으면 종료
        } catch (error: any) {
          // 사용자가 취소한 경우는 아무것도 하지 않음
          if (error.name === 'AbortError') {
            return;
          }
          // 오류 발생 시 경고 알림
          alert('파일 저장 중 오류가 발생했습니다. 브라우저가 File System Access API를 지원하지 않거나 권한이 필요합니다.');
          return;
        }
      }
      
      // File System Access API 미지원 시 경고 알림
      alert('이 브라우저는 File System Access API를 지원하지 않습니다. Chrome, Edge, Opera 등의 최신 브라우저를 사용해주세요.');
      return;
    } catch (error) {
      console.error('프로젝트 저장 실패:', error);
      alert('프로젝트 저장 중 오류가 발생했습니다.');
    }
  }, [objects, zOrder, initialArtboards, selectedId, projectName]);

  const isRestoringHistoryRef = React.useRef(false);
  
  // 작업 상태 추적 (드래그, 리사이즈 등)
  const isDraggingRef = React.useRef(false);
  const isResizingRef = React.useRef(false);
  const isRotatingRef = React.useRef(false);
  const isDrawingRef = React.useRef(false);
  const isChangingColorRef = React.useRef(false); // 컬러피커 드래그 중 추적
  
  // 이전 작업 상태 추적 (작업 완료 시점 감지용)
  const prevIsDraggingRef = React.useRef(false);
  const prevIsResizingRef = React.useRef(false);
  const prevIsRotatingRef = React.useRef(false);
  const prevIsDrawingRef = React.useRef(false);
  
  // 이전 상태를 ref로 저장 (변경 감지용)
  const prevObjectsRef = React.useRef(objects);
  const prevZOrderRef = React.useRef(zOrder);
  const prevSelectedIdRef = React.useRef(selectedId);
  const hasInitialStateSavedRef = React.useRef(false);

  // 이미지 레지스트리 (같은 이미지 재사용으로 메모리 최적화)
  const imageRegistry = useImageRegistry();

  // 새 프로젝트 핸들러
  const handleNewProject = React.useCallback(() => {
    // 확인 다이얼로그
    const confirmed = window.confirm('새 프로젝트를 시작하시겠습니까?\n\n현재 작업 내용이 모두 삭제됩니다.');
    if (!confirmed) return;

    // 캐시 초기화
    clearAllCaches();
    imageRegistry.clearRegistry();

    // 객체 및 아트보드 초기화
    const emptyObjects = new Map<string, CanvasObject>();
    const emptyZOrder: string[] = [];
    
    setObjects(emptyObjects);
    setZOrder(emptyZOrder);
    setArtboards([...defaultArtboards]); // 기본 아트보드로 복원
    clearSelection();
    setSelectedId(null);

    // 프로젝트 정보 초기화
    setProjectName('');
    setLoadedTemplateName('');

    // 상태 복원 및 히스토리 저장
    restoreState({
      objects: emptyObjects,
      zOrder: emptyZOrder,
      selectedId: null
    });
    saveToHistory({
      objects: emptyObjects,
      zOrder: emptyZOrder,
      selectedId: null
    });

    // 렌더링 요청
    requestRender();
    setLayerFilterResetKey((prev) => prev + 1);
  }, [clearAllCaches, imageRegistry, setObjects, setZOrder, setArtboards, clearSelection, setSelectedId, setProjectName, setLoadedTemplateName, restoreState, saveToHistory, requestRender]);

  // 프로젝트 불러오기 함수 (ZIP 파일에서 불러오기)
  const handleLoadProject = React.useCallback(async () => {
    try {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.zip,application/zip';
      input.multiple = true; // 여러 파일 선택 허용
      
      input.onchange = async (e: any) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;
        
        // 1개 선택 시 그대로 사용, 여러 개 선택 시 랜덤으로 1개 선택
        const file = files.length === 1 
          ? files[0] 
          : files[Math.floor(Math.random() * files.length)];
        
        if (!file) return;
        
        try {
          // 모든 캐시 초기화 (이전 템플릿의 캐시 정리)
          clearAllCaches();
          imageRegistry.clearRegistry();
          
          // ZIP 파일 압축 해제
          const zip = new JSZip();
          const zipData = await zip.loadAsync(file);
          
          // project.json 파일 읽기
          const projectJsonFile = zipData.file('project.json');
          if (!projectJsonFile) {
            alert('프로젝트 파일이 올바르지 않습니다. (project.json을 찾을 수 없습니다)');
            return;
          }
          
          const text = await projectJsonFile.async('string');
          const projectData = JSON.parse(text);
          
          // 프로젝트 데이터 검증
          if (!projectData.objects || !Array.isArray(projectData.objects)) {
            alert('잘못된 프로젝트 파일입니다.');
            return;
          }
          
          // images 폴더의 이미지 파일들을 Map으로 저장 (인덱스로 매핑)
          const imageFilesMap = new Map<number, Blob>();
          const imageFiles = Object.keys(zipData.files).filter(name => 
            name.startsWith('images/') && name !== 'images/'
          );
          
          for (const imagePath of imageFiles) {
            const imageFile = zipData.file(imagePath);
            if (imageFile) {
              // 파일명에서 인덱스 추출: images/image_0.png -> 0
              const match = imagePath.match(/image_(\d+)\./);
              if (match) {
                const idx = parseInt(match[1], 10);
                const blob = await imageFile.async('blob');
                imageFilesMap.set(idx, blob);
              }
            }
          }
          
          // 이미지 객체 로드 및 레지스트리 등록
          const loadedObjects = new Map<string, CanvasObject>();
          for (const objData of projectData.objects) {
            const obj = objData as CanvasObject;
            
            // 이미지 객체인 경우 ZIP에서 이미지 로드
            if (obj.type === 'image') {
              try {
                // 객체에 저장된 이미지 인덱스 정보 사용
                const imageIndex = (obj as any)._imageIndex;
                let imageBlob: Blob | null = null;
                
                if (imageIndex !== undefined && typeof imageIndex === 'number') {
                  imageBlob = imageFilesMap.get(imageIndex) || null;
                }
                
                // 인덱스를 찾지 못한 경우 imageSrc에서 인덱스 추출 시도
                if (!imageBlob && obj.imageSrc) {
                  const srcMatch = obj.imageSrc.match(/image_(\d+)\./);
                  if (srcMatch) {
                    const idx = parseInt(srcMatch[1], 10);
                    imageBlob = imageFilesMap.get(idx) || null;
                  }
                }
                
                // 여전히 찾지 못한 경우 첫 번째 이미지 사용
                if (!imageBlob && imageFilesMap.size > 0) {
                  const firstIndex = Math.min(...Array.from(imageFilesMap.keys()));
                  imageBlob = imageFilesMap.get(firstIndex) || null;
                }
                
                if (imageBlob) {
                  const imageUrl = URL.createObjectURL(imageBlob);
                  const img = new Image();
                  await new Promise<void>((resolve) => {
                    img.onload = () => {
                      // 이미지 레지스트리에 등록
                      const hash = (obj as any).imageHash || imageUrl;
                      imageRegistry.registerImage(hash, {
                        image: img,
                        src: imageUrl,
                        fileName: (obj as any).fileName,
                        mimeType: imageBlob!.type
                      });
                      
                      loadedObjects.set(obj.id, {
                        ...obj,
                        image: img,
                        imageSrc: imageUrl
                      });
                      resolve();
                    };
                    img.onerror = () => {
                      console.error('이미지 로드 실패:', imageUrl);
                      // 이미지 로드 실패해도 객체는 추가
                      loadedObjects.set(obj.id, obj);
                      resolve();
                    };
                    img.src = imageUrl;
                  });
                } else {
                  // ZIP에 이미지가 없는 경우 원본 imageSrc 사용 시도
                  if (obj.imageSrc) {
                    const img = new Image();
                    await new Promise<void>((resolve) => {
                      img.onload = () => {
                        const hash = (obj as any).imageHash || obj.imageSrc || '';
                        if (hash) {
                          imageRegistry.registerImage(hash, {
                            image: img,
                            src: obj.imageSrc!,
                            fileName: (obj as any).fileName,
                            mimeType: (obj as any).mimeType
                          });
                        }
                        loadedObjects.set(obj.id, { ...obj, image: img });
                        resolve();
                      };
                      img.onerror = () => {
                        console.error('이미지 로드 실패:', obj.imageSrc);
                        loadedObjects.set(obj.id, obj);
                        resolve();
                      };
                      img.src = obj.imageSrc!;
                    });
                  } else {
                    loadedObjects.set(obj.id, obj);
                  }
                }
              } catch (error) {
                console.error('이미지 로드 중 오류:', error);
                loadedObjects.set(obj.id, obj);
              }
            } else {
              loadedObjects.set(obj.id, obj);
            }
          }
          
          // 아트보드 복원 (저장된 아트보드가 있으면 사용, 없으면 초기 아트보드 사용)
          if (projectData.artboards && Array.isArray(projectData.artboards) && projectData.artboards.length > 0) {
            setArtboards(projectData.artboards);
          }
          
          // 상태 복원
          restoreState({
            objects: loadedObjects,
            zOrder: projectData.zOrder || [],
            selectedId: projectData.selectedId || null
          });
          
          // 히스토리 초기화 및 현재 상태 저장
          saveToHistory({
            objects: loadedObjects,
            zOrder: projectData.zOrder || [],
            selectedId: projectData.selectedId || null
          });
          
          requestRender();
          setLayerFilterResetKey((prev) => prev + 1);
          
          // ZIP 파일명 저장 (확장자 제거)
          const fileName = file.name;
          const nameWithoutExt = fileName.replace(/\.zip$/i, '');
          setLoadedTemplateName(nameWithoutExt);
          
          //alert('프로젝트를 불러왔습니다.');
        } catch (error) {
          console.error('프로젝트 불러오기 실패:', error);
          alert('프로젝트 파일을 불러올 수 없습니다.');
        }
      };
      
      input.click();
    } catch (error) {
      console.error('파일 선택 실패:', error);
      alert('파일 선택 중 오류가 발생했습니다.');
    }
  }, [restoreState, saveToHistory, requestRender, imageRegistry, setArtboards]);

  /**
   * 템플릿 파일 불러오기 (드래그 앤 드롭용)
   * @param file ZIP 파일
   * @param targetArtboardId 아트보드 ID (null이면 전체 불러오기, 있으면 해당 아트보드만 교체)
   */
  const handleLoadTemplateFile = React.useCallback(async (
    file: File,
    targetArtboardId: string | null
  ) => {
    try {
      // 모든 캐시 초기화 (이전 템플릿의 캐시 정리)
      // 부분 불러오기일 때는 캐시를 초기화하지 않음 (다른 아트보드의 이미지 유지)
      if (!targetArtboardId) {
        clearAllCaches();
        imageRegistry.clearRegistry();
      }
      
      // ZIP 파일 압축 해제
      const zip = new JSZip();
      const zipData = await zip.loadAsync(file);
      
      // project.json 파일 읽기
      const projectJsonFile = zipData.file('project.json');
      if (!projectJsonFile) {
        alert('프로젝트 파일이 올바르지 않습니다. (project.json을 찾을 수 없습니다)');
        return;
      }
      
      const text = await projectJsonFile.async('string');
      const projectData = JSON.parse(text);
      
      // 프로젝트 데이터 검증
      if (!projectData.objects || !Array.isArray(projectData.objects)) {
        alert('잘못된 프로젝트 파일입니다.');
        return;
      }
      
      // images 폴더의 이미지 파일들을 Map으로 저장 (인덱스로 매핑)
      const imageFilesMap = new Map<number, Blob>();
      const imageFiles = Object.keys(zipData.files).filter(name => 
        name.startsWith('images/') && name !== 'images/'
      );
      
      for (const imagePath of imageFiles) {
        const imageFile = zipData.file(imagePath);
        if (imageFile) {
          // 파일명에서 인덱스 추출: images/image_0.png -> 0
          const match = imagePath.match(/image_(\d+)\./);
          if (match) {
            const idx = parseInt(match[1], 10);
            const blob = await imageFile.async('blob');
            imageFilesMap.set(idx, blob);
          }
        }
      }
      
      // 전체 불러오기 모드
      if (!targetArtboardId) {
        // 이미지 객체 로드 및 레지스트리 등록
        const loadedObjects = new Map<string, CanvasObject>();
        for (const objData of projectData.objects) {
          const obj = objData as CanvasObject;
          
          // 이미지 객체인 경우 ZIP에서 이미지 로드
          if (obj.type === 'image') {
            try {
              // 객체에 저장된 이미지 인덱스 정보 사용
              const imageIndex = (obj as any)._imageIndex;
              let imageBlob: Blob | null = null;
              
              if (imageIndex !== undefined && typeof imageIndex === 'number') {
                imageBlob = imageFilesMap.get(imageIndex) || null;
              }
              
              // 인덱스를 찾지 못한 경우 imageSrc에서 인덱스 추출 시도
              if (!imageBlob && obj.imageSrc) {
                const srcMatch = obj.imageSrc.match(/image_(\d+)\./);
                if (srcMatch) {
                  const idx = parseInt(srcMatch[1], 10);
                  imageBlob = imageFilesMap.get(idx) || null;
                }
              }
              
              // 여전히 찾지 못한 경우 첫 번째 이미지 사용
              if (!imageBlob && imageFilesMap.size > 0) {
                const firstIndex = Math.min(...Array.from(imageFilesMap.keys()));
                imageBlob = imageFilesMap.get(firstIndex) || null;
              }
              
              if (imageBlob) {
                const imageUrl = URL.createObjectURL(imageBlob);
                const img = new Image();
                await new Promise<void>((resolve) => {
                  img.onload = () => {
                    // 이미지 레지스트리에 등록
                    const hash = (obj as any).imageHash || imageUrl;
                    imageRegistry.registerImage(hash, {
                      image: img,
                      src: imageUrl,
                      fileName: (obj as any).fileName,
                      mimeType: imageBlob!.type
                    });
                    
                    loadedObjects.set(obj.id, {
                      ...obj,
                      image: img,
                      imageSrc: imageUrl
                    });
                    resolve();
                  };
                  img.onerror = () => {
                    console.error('이미지 로드 실패:', imageUrl);
                    // 이미지 로드 실패해도 객체는 추가
                    loadedObjects.set(obj.id, obj);
                    resolve();
                  };
                  img.src = imageUrl;
                });
              } else {
                // ZIP에 이미지가 없는 경우 원본 imageSrc 사용 시도
                if (obj.imageSrc) {
                  const img = new Image();
                  await new Promise<void>((resolve) => {
                    img.onload = () => {
                      const hash = (obj as any).imageHash || obj.imageSrc || '';
                      if (hash) {
                        imageRegistry.registerImage(hash, {
                          image: img,
                          src: obj.imageSrc!,
                          fileName: (obj as any).fileName,
                          mimeType: (obj as any).mimeType
                        });
                      }
                      loadedObjects.set(obj.id, { ...obj, image: img });
                      resolve();
                    };
                    img.onerror = () => {
                      console.error('이미지 로드 실패:', obj.imageSrc);
                      loadedObjects.set(obj.id, obj);
                      resolve();
                    };
                    img.src = obj.imageSrc!;
                  });
                } else {
                  loadedObjects.set(obj.id, obj);
                }
              }
            } catch (error) {
              console.error('이미지 로드 중 오류:', error);
              loadedObjects.set(obj.id, obj);
            }
          } else {
            loadedObjects.set(obj.id, obj);
          }
        }
        
        // 아트보드 복원 (저장된 아트보드가 있으면 사용, 없으면 초기 아트보드 사용)
        if (projectData.artboards && Array.isArray(projectData.artboards) && projectData.artboards.length > 0) {
          setArtboards(projectData.artboards);
        }
        
        // 상태 복원
        restoreState({
          objects: loadedObjects,
          zOrder: projectData.zOrder || [],
          selectedId: projectData.selectedId || null
        });
        
        // 히스토리 초기화 및 현재 상태 저장
        saveToHistory({
          objects: loadedObjects,
          zOrder: projectData.zOrder || [],
          selectedId: projectData.selectedId || null
        });
        
        requestRender();
        setLayerFilterResetKey((prev) => prev + 1);
        
        // ZIP 파일명 저장 (확장자 제거)
        const fileName = file.name;
        const nameWithoutExt = fileName.replace(/\.zip$/i, '');
        setLoadedTemplateName(nameWithoutExt);
      } else {
        // 부분 불러오기 모드: 특정 아트보드만 교체
        // ZIP에서 타겟 아트보드 찾기
        const targetArtboard = projectData.artboards?.find((ab: Artboard) => ab.id === targetArtboardId);
        if (!targetArtboard) {
          alert('해당 아트보드를 찾을 수 없습니다.');
          return;
        }
        
        // 타겟 아트보드에 속한 객체들 필터링
        const targetObjects = projectData.objects.filter((obj: CanvasObject) => {
          // 직접 속한 객체
          if (obj.artboardId === targetArtboardId) {
            return true;
          }
          // 클리핑 마스크로 연결된 객체 (마스크가 타겟 아트보드에 속한 경우)
          if (obj.isClipped && obj.clipMaskId) {
            const maskObj = projectData.objects.find((o: CanvasObject) => o.id === obj.clipMaskId);
            if (maskObj && maskObj.artboardId === targetArtboardId) {
              return true;
            }
          }
          return false;
        });
        
        // 타겟 아트보드에 속한 객체 ID Set
        const targetObjectIds = new Set(targetObjects.map((obj: CanvasObject) => obj.id));
        
        // 이미지 객체 로드 및 레지스트리 등록
        const loadedObjects = new Map<string, CanvasObject>();
        for (const objData of targetObjects) {
          const obj = objData as CanvasObject;
          
          // 이미지 객체인 경우 ZIP에서 이미지 로드
          if (obj.type === 'image') {
            try {
              const imageIndex = (obj as any)._imageIndex;
              let imageBlob: Blob | null = null;
              
              if (imageIndex !== undefined && typeof imageIndex === 'number') {
                imageBlob = imageFilesMap.get(imageIndex) || null;
              }
              
              if (!imageBlob && obj.imageSrc) {
                const srcMatch = obj.imageSrc.match(/image_(\d+)\./);
                if (srcMatch) {
                  const idx = parseInt(srcMatch[1], 10);
                  imageBlob = imageFilesMap.get(idx) || null;
                }
              }
              
              if (!imageBlob && imageFilesMap.size > 0) {
                const firstIndex = Math.min(...Array.from(imageFilesMap.keys()));
                imageBlob = imageFilesMap.get(firstIndex) || null;
              }
              
              if (imageBlob) {
                const imageUrl = URL.createObjectURL(imageBlob);
                const img = new Image();
                await new Promise<void>((resolve) => {
                  img.onload = () => {
                    const hash = (obj as any).imageHash || imageUrl;
                    imageRegistry.registerImage(hash, {
                      image: img,
                      src: imageUrl,
                      fileName: (obj as any).fileName,
                      mimeType: imageBlob!.type
                    });
                    
                    loadedObjects.set(obj.id, {
                      ...obj,
                      image: img,
                      imageSrc: imageUrl
                    });
                    resolve();
                  };
                  img.onerror = () => {
                    console.error('이미지 로드 실패:', imageUrl);
                    loadedObjects.set(obj.id, obj);
                    resolve();
                  };
                  img.src = imageUrl;
                });
              } else {
                if (obj.imageSrc) {
                  const img = new Image();
                  await new Promise<void>((resolve) => {
                    img.onload = () => {
                      const hash = (obj as any).imageHash || obj.imageSrc || '';
                      if (hash) {
                        imageRegistry.registerImage(hash, {
                          image: img,
                          src: obj.imageSrc!,
                          fileName: (obj as any).fileName,
                          mimeType: (obj as any).mimeType
                        });
                      }
                      loadedObjects.set(obj.id, { ...obj, image: img });
                      resolve();
                    };
                    img.onerror = () => {
                      console.error('이미지 로드 실패:', obj.imageSrc);
                      loadedObjects.set(obj.id, obj);
                      resolve();
                    };
                    img.src = obj.imageSrc!;
                  });
                } else {
                  loadedObjects.set(obj.id, obj);
                }
              }
            } catch (error) {
              console.error('이미지 로드 중 오류:', error);
              loadedObjects.set(obj.id, obj);
            }
          } else {
            loadedObjects.set(obj.id, obj);
          }
        }
        
        // 기존 객체에서 타겟 아트보드에 속한 객체들 제거
        const existingObjects = new Map(objects);
        const existingZOrder = [...zOrder];
        
        // 제거할 객체 ID 수집
        const objectsToRemove = new Set<string>();
        existingObjects.forEach((obj, objId) => {
          if (obj.artboardId === targetArtboardId) {
            objectsToRemove.add(objId);
          }
          // 클리핑 마스크로 연결된 객체도 제거
          if (obj.isClipped && obj.clipMaskId && objectsToRemove.has(obj.clipMaskId)) {
            objectsToRemove.add(objId);
          }
        });
        
        // 객체 제거
        objectsToRemove.forEach(objId => {
          existingObjects.delete(objId);
        });
        
        // 기존 아트보드 찾기 (좌표 차이 계산용)
        const existingArtboard = artboards.find(ab => ab.id === targetArtboardId);
        
        // 좌표 차이 계산 (기존 아트보드 위치로 이동하기 위한 offset)
        let dx = 0;
        let dy = 0;
        if (existingArtboard) {
          dx = existingArtboard.x - targetArtboard.x;
          dy = existingArtboard.y - targetArtboard.y;
        }
        
        // 새 객체 추가 (좌표 차이만큼 이동)
        loadedObjects.forEach((obj, objId) => {
          existingObjects.set(objId, {
            ...obj,
            x: obj.x + dx,
            y: obj.y + dy
          });
        });
        
        // zOrder 업데이트: 제거된 객체 ID 제거 후 새 객체 ID 추가
        const newZOrder = existingZOrder.filter(id => !objectsToRemove.has(id));
        // ZIP 파일의 원본 zOrder에서 타겟 아트보드에 속한 객체들만 필터링 (순서 유지)
        const templateZOrder = (projectData.zOrder || []).filter((id: string) => targetObjectIds.has(id));
        // 템플릿의 zOrder 순서를 유지하면서 끝에 추가
        templateZOrder.forEach((id: string) => {
          if (!newZOrder.includes(id)) {
            newZOrder.push(id);
          }
        });
        
        // 아트보드 업데이트: 타겟 아트보드만 교체 (기존 좌표로 이동)
        const updatedArtboards = artboards.map(ab => 
          ab.id === targetArtboardId 
            ? { ...targetArtboard, x: existingArtboard?.x ?? targetArtboard.x, y: existingArtboard?.y ?? targetArtboard.y }
            : ab
        );
        setArtboards(updatedArtboards);
        
        // 상태 복원
        restoreState({
          objects: existingObjects,
          zOrder: newZOrder,
          selectedId: null
        });
        
        // 히스토리 저장
        saveToHistory({
          objects: existingObjects,
          zOrder: newZOrder,
          selectedId: null
        });
        
        requestRender();
        setLayerFilterResetKey((prev) => prev + 1);
      }
    } catch (error) {
      console.error('템플릿 불러오기 실패:', error);
      alert('템플릿 파일을 불러올 수 없습니다.');
    }
  }, [restoreState, saveToHistory, requestRender, imageRegistry, setArtboards, objects, zOrder, artboards]);

  // 템플릿 갤러리에서 템플릿 선택 핸들러
  const handleSelectTemplateFromGallery = React.useCallback(async (templateName: string, templateUrl?: string) => {
    try {
      // URL이 전달되지 않은 경우 URL 생성
      let zipUrl = templateUrl;
      if (!zipUrl) {
        // templateConfig에서 URL 생성 함수 import
        const { getTemplateZipUrl } = await import('../../config/templateConfig');
        zipUrl = getTemplateZipUrl(templateName);
      }

      if (!zipUrl) {
        throw new Error('템플릿 URL을 생성할 수 없습니다.');
      }

      // ZIP 파일 다운로드
      const response = await fetch(zipUrl);
      if (!response.ok) {
        throw new Error(`템플릿 파일을 불러올 수 없습니다. (${response.status})`);
      }

      const blob = await response.blob();
      const zipFile = new File([blob], `${templateName}.zip`, { type: 'application/zip' });

      // 템플릿 불러오기
      await handleLoadTemplateFile(zipFile, null);
    } catch (err: any) {
      console.error('템플릿 갤러리에서 불러오기 실패:', err);
      alert(`템플릿을 불러올 수 없습니다.\n\n${err.message || '알 수 없는 오류가 발생했습니다.'}`);
    }
  }, [handleLoadTemplateFile]);

  // 클리핑 마스크 맵 (성능 최적화: Map 기반 O(1) 조회)
  const clipMaskMap = useClipMask(objects, zOrder);

  // 클리핑 마스크 관계 관리
  const { updateClipMaskRelations, toggleClipMask } = useClipMaskRelations({
    objects,
    zOrder,
    updateObject
  });

  // 아트보드 Map 생성 (성능 최적화: O(1) 조회)
  // 캔버스 내 사용된 텍스트의 유니크 목록 생성
  const uniqueTexts = React.useMemo(() => {
    const set = new Set<string>();
    objects.forEach((obj) => {
      if (obj.type === 'text' && obj.text) {
        set.add(obj.text);
      }
    });
    return Array.from(set);
  }, [objects]);

  const artboardsMap = React.useMemo(() => {
    const map = new Map<string, Artboard>();
    initialArtboards.forEach(ab => map.set(ab.id, ab));
    return map;
  }, [initialArtboards]);

  // 아트보드 자동 할당 함수
  const updateArtboardAssignments = React.useCallback(() => {
    setObjects(prev => {
      const next = new Map(prev);
      
      // 1단계: 부모(및 비클리핑 객체)부터 최적 아트보드 계산
      const firstPass = new Map<string, CanvasObject>();
      prev.forEach((obj, objId) => {
        if (obj.isClipped && obj.clipMaskId) {
          // 클리핑된 객체는 2단계에서 처리
          firstPass.set(objId, obj);
        } else if (obj.artboardLocked) {
          // 아트보드가 고정된 객체는 변경하지 않음
          firstPass.set(objId, obj);
        } else {
          // 비클리핑 객체: 최적 아트보드 계산
          const bestArtboardId = getBestArtboardForObject(obj, initialArtboards);
          if (obj.artboardId !== bestArtboardId) {
            firstPass.set(objId, { ...obj, artboardId: bestArtboardId || undefined });
          } else {
            firstPass.set(objId, obj);
          }
        }
      });
      
      // 2단계: 클리핑된 객체(자식)는 부모의 아트보드를 상속
      const secondPass = new Map<string, CanvasObject>();
      firstPass.forEach((obj, objId) => {
        if (obj.isClipped && obj.clipMaskId) {
          const parent = firstPass.get(obj.clipMaskId);
          const parentArtboardId = parent?.artboardId;
          if (obj.artboardId !== parentArtboardId) {
            secondPass.set(objId, { ...obj, artboardId: parentArtboardId || undefined });
          } else {
            secondPass.set(objId, obj);
          }
        } else {
          secondPass.set(objId, obj);
        }
      });
      
      return secondPass;
    });
  }, [initialArtboards]);

  // 드래그 오버 상태
  const [dragOverImageId, setDragOverImageId] = React.useState<string | null>(null);
  const [dragOverImageHash, setDragOverImageHash] = React.useState<string | null>(null);
  const [dragOverArtboardId, setDragOverArtboardId] = React.useState<string | null>(null);
  
  // 드래그 선택 영역 상태
  const [dragSelectRect, setDragSelectRect] = React.useState<{ x: number; y: number; width: number; height: number } | null>(null);
  
  // 레이어 드래그 상태
  const [draggingLayerId, setDraggingLayerId] = React.useState<string | null>(null);
  const [dragOverLayerId, setDragOverLayerId] = React.useState<string | null>(null);
  const [dropAbove, setDropAbove] = React.useState<boolean>(false);
  
  // 객체 생성 시 아트보드 할당을 위한 래핑 함수
  const commitRectWithArtboard = React.useCallback(() => {
    commitRect();
    // 객체 생성 후 아트보드 할당 (다음 렌더링 사이클에서 처리)
    React.startTransition(() => {
      updateArtboardAssignments();
    });
  }, [commitRect, updateArtboardAssignments]);
  
  const commitCircleWithArtboard = React.useCallback(() => {
    commitCircle();
    // 객체 생성 후 아트보드 할당 (다음 렌더링 사이클에서 처리)
    React.startTransition(() => {
      updateArtboardAssignments();
    });
  }, [commitCircle, updateArtboardAssignments]);
  
  useRectDraw(
    canvasRef,
    () => canvasState,
    beginRect,
    updateRect,
    commitRectWithArtboard,
    beginCircle,
    updateCircle,
    commitCircleWithArtboard,
    clearTemp,
    () => requestRender(),
    () => activeTool,
    () => getIsPanning(),
    isDrawingRef
  );

  // 선택 툴 (다중 선택 로직이 useMoveSelected에 통합되어 있으므로 주석 처리)
  // useSelect(
  //   canvasRef,
  //   () => canvasState,
  //   () => activeTool,
  //   hitTestPoint,
  //   setSelectedId,
  //   requestRender
  // );

  // 선택된 객체 변경 시 입력값 동기화 (성능 최적화: 객체 값 변경 추적)
  const selectedObject = React.useMemo(() => {
    if (!selectedId) return null;
    return objects.get(selectedId) || null;
  }, [selectedId, objects]);
  
  // 객체 선택 시 아트보드 선택 해제
  React.useEffect(() => {
    if (selectedId || (selectedIds && selectedIds.size > 0)) {
      setSelectedArtboardId(null);
    }
  }, [selectedId, selectedIds]);

  // 현재 선택된 객체들의 색상 계산 (단일/다중 선택 모두 지원)
  const currentColor = React.useMemo(() => {
    const selectedObjs = getSelectedObjects();
    if (selectedObjs.length === 0) return defaultColor; // 선택된 객체가 없을 때는 기본 색상
    
    // 다중 선택 시: 텍스트 객체가 있으면 텍스트 객체의 color 우선, 없으면 첫 번째 객체의 backgroundColor
    if (selectedObjs.length > 1) {
      const textObj = selectedObjs.find(obj => obj.type === 'text');
      if (textObj && textObj.color) {
        return textObj.color;
      }
      return selectedObjs[0].backgroundColor || defaultColor;
    }
    
    // 단일 선택 시
    const obj = selectedObjs[0];
    if (obj.type === 'text') {
      return obj.color || '#000000';
    }
    return obj.backgroundColor || defaultColor;
  }, [selectedId, selectedIds, objects, getSelectedObjects, defaultColor]);

  // 색상 ID 매핑 관리 (색상키 -> 고유ID)
  // 색상키 형식: `${type}_${normalizedColor}` (예: "background_FF0000")
  const colorIdMapRef = React.useRef<Map<string, string>>(new Map());

  // 고유 ID 생성 함수
  const generateColorId = React.useCallback((type: UsedColor['type'], color: string): string => {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 8);
    return `${type}_${timestamp}_${random}`;
  }, []);

  // 특정 타입의 모든 객체 색상 변경 함수 (히스토리 저장 옵션)
  const updateObjectsByColorType = React.useCallback((
    type: UsedColor['type'],
    oldColor: string,
    newColor: string,
    saveHistory: boolean = true
  ) => {
    const normalizedOld = oldColor.toUpperCase();
    
    getObjectsArray().forEach(obj => {
      switch (type) {
        case 'background':
          if (obj.type !== 'text' && obj.backgroundColor?.toUpperCase() === normalizedOld) {
            updateObject(obj.id, (o) => ({ ...o, backgroundColor: newColor }));
          }
          break;
        case 'text':
          if (obj.type === 'text' && obj.color?.toUpperCase() === normalizedOld) {
            updateObject(obj.id, (o) => ({ ...o, color: newColor }));
          }
          break;
        case 'border':
          if (obj.borderColor?.toUpperCase() === normalizedOld && obj.borderWidth && obj.borderWidth > 0) {
            updateObject(obj.id, (o) => ({ ...o, borderColor: newColor }));
          }
          break;
        case 'outglow':
          if (obj.outGlowEnabled && obj.outGlowColor?.toUpperCase() === normalizedOld) {
            updateObject(obj.id, (o) => ({ ...o, outGlowColor: newColor }));
          }
          break;
      }
    });
    
    requestRender();
    
    if (saveHistory) {
      saveToHistory({ objects, zOrder, selectedId });
    }
  }, [getObjectsArray, updateObject, requestRender, saveToHistory, objects, zOrder, selectedId]);

  // 사용된 색상 목록 계산 (고유 ID 포함)
  const usedColors = React.useMemo(() => {
    const colorMap = new Map<string, UsedColor>();
    const idMap = colorIdMapRef.current;
    const activeColorKeys = new Set<string>();
    
    // 모든 객체 순회하며 색상 수집
    getObjectsArray().forEach(obj => {
      // 배경색 (텍스트가 아닌 객체)
      if (obj.type !== 'text' && obj.backgroundColor) {
        const normalized = obj.backgroundColor.toUpperCase();
        const colorKey = `background_${normalized}`;
        activeColorKeys.add(colorKey);
        
        let usedColor = colorMap.get(colorKey);
        if (!usedColor) {
          // 기존 ID가 있으면 재사용, 없으면 새로 생성
          let id = idMap.get(colorKey);
          if (!id) {
            id = generateColorId('background', normalized);
            idMap.set(colorKey, id);
          }
          usedColor = {
            id,
            type: 'background',
            color: normalized,
            count: 0
          };
          colorMap.set(colorKey, usedColor);
        }
        usedColor.count++;
      }
      
      // 텍스트 색상
      if (obj.type === 'text' && obj.color) {
        const normalized = obj.color.toUpperCase();
        const colorKey = `text_${normalized}`;
        activeColorKeys.add(colorKey);
        
        let usedColor = colorMap.get(colorKey);
        if (!usedColor) {
          let id = idMap.get(colorKey);
          if (!id) {
            id = generateColorId('text', normalized);
            idMap.set(colorKey, id);
          }
          usedColor = {
            id,
            type: 'text',
            color: normalized,
            count: 0
          };
          colorMap.set(colorKey, usedColor);
        }
        usedColor.count++;
      }
      
      // 테두리 색상 (테두리 두께가 0이 아닐 때만)
      if (obj.borderColor && obj.borderWidth && obj.borderWidth > 0) {
        const normalized = obj.borderColor.toUpperCase();
        const colorKey = `border_${normalized}`;
        activeColorKeys.add(colorKey);
        
        let usedColor = colorMap.get(colorKey);
        if (!usedColor) {
          let id = idMap.get(colorKey);
          if (!id) {
            id = generateColorId('border', normalized);
            idMap.set(colorKey, id);
          }
          usedColor = {
            id,
            type: 'border',
            color: normalized,
            count: 0
          };
          colorMap.set(colorKey, usedColor);
        }
        usedColor.count++;
      }
      
      // 아웃글로우 색상 (아웃글로우가 활성화되어 있을 때만)
      if (obj.outGlowEnabled && obj.outGlowColor) {
        const normalized = obj.outGlowColor.toUpperCase();
        const colorKey = `outglow_${normalized}`;
        activeColorKeys.add(colorKey);
        
        let usedColor = colorMap.get(colorKey);
        if (!usedColor) {
          let id = idMap.get(colorKey);
          if (!id) {
            id = generateColorId('outglow', normalized);
            idMap.set(colorKey, id);
          }
          usedColor = {
            id,
            type: 'outglow',
            color: normalized,
            count: 0
          };
          colorMap.set(colorKey, usedColor);
        }
        usedColor.count++;
      }
    });
    
    // 사용되지 않는 ID 정리
    idMap.forEach((id, colorKey) => {
      if (!activeColorKeys.has(colorKey)) {
        idMap.delete(colorKey);
      }
    });
    
    // Map을 배열로 변환하여 반환
    return Array.from(colorMap.values());
  }, [objects, zOrder, getObjectsArray, generateColorId]);
  
  // 선택된 객체의 테두리 두께 값 (성능 최적화: 메모이제이션)
  const selectedBorderWidth = React.useMemo(() => {
    const selectedObjs = getSelectedObjects();
    if (selectedObjs.length === 0) return 0;
    if (tempBorderWidth !== '') {
      const num = Number(tempBorderWidth);
      return isNaN(num) ? 0 : num;
    }
    return selectedObjs[0].borderWidth ?? 0;
  }, [selectedObject?.id, selectedObject?.borderWidth, tempBorderWidth, getSelectedObjects]);
  
  // 선택된 객체의 아웃글로우 투명도 값 (성능 최적화: 메모이제이션)
  const selectedOutGlowOpacity = React.useMemo(() => {
    const selectedObjs = getSelectedObjects();
    if (selectedObjs.length === 0) return 100;
    if (tempOutGlowOpacity !== '') {
      const num = Number(tempOutGlowOpacity);
      return isNaN(num) ? 100 : num;
    }
    return selectedObjs[0].outGlowOpacity ?? 100;
  }, [selectedObject?.id, selectedObject?.outGlowOpacity, tempOutGlowOpacity, getSelectedObjects]);
  
  // 선택된 객체의 아웃글로우 크기 값 (성능 최적화: 메모이제이션)
  const selectedOutGlowSize = React.useMemo(() => {
    const selectedObjs = getSelectedObjects();
    if (selectedObjs.length === 0) return 10;
    if (tempOutGlowSize !== '') {
      const num = Number(tempOutGlowSize);
      return isNaN(num) ? 10 : num;
    }
    return selectedObjs[0].outGlowSize ?? 10;
  }, [selectedObject?.id, selectedObject?.outGlowSize, tempOutGlowSize, getSelectedObjects]);
  
  // 선택된 객체의 아웃글로우 스프레드 값 (성능 최적화: 메모이제이션)
  const selectedOutGlowSpread = React.useMemo(() => {
    const selectedObjs = getSelectedObjects();
    if (selectedObjs.length === 0) return 0;
    if (tempOutGlowSpread !== '') {
      const num = Number(tempOutGlowSpread);
      return isNaN(num) ? 0 : num;
    }
    return selectedObjs[0].outGlowSpread ?? 0;
  }, [selectedObject?.id, selectedObject?.outGlowSpread, tempOutGlowSpread, getSelectedObjects]);

  // 선택된 객체의 블러 반경 값 (성능 최적화: 메모이제이션)
  const selectedBlurRadius = React.useMemo(() => {
    const selectedObjs = getSelectedObjects();
    if (selectedObjs.length === 0) return 10;
    if (tempBlurRadius !== '') {
      const num = Number(tempBlurRadius);
      if (!isNaN(num)) return Math.max(0, Math.min(200, num));
    }
    return selectedObjs[0].blurRadius ?? 10;
  }, [selectedObject?.id, selectedObject?.blurRadius, tempBlurRadius, getSelectedObjects]);
  
  // 이전 값 추적 (불필요한 업데이트 방지)
  const prevSelectedBorderWidthRef = React.useRef<number | null>(null);
  const prevSelectedOutGlowOpacityRef = React.useRef<number | null>(null);
  const prevSelectedOutGlowSizeRef = React.useRef<number | null>(null);
  const prevSelectedOutGlowSpreadRef = React.useRef<number | null>(null);
  const prevSelectedBlurRadiusRef = React.useRef<number | null>(null);
  
  // 선택된 객체의 실제 값들을 추적하여 변경 시에만 입력값 업데이트
  // 아트보드에 속한 객체는 상대 좌표로 표시 (성능 최적화: 필요한 값만 의존성에 포함)
  // 회전이 있는 경우 AABB 기준 좌표 표시
  const selectedObjectValues = React.useMemo(() => {
    if (!selectedObject) return null;
    
    // 회전 반영 AABB 계산 (회전이 없어도 일관성 유지)
    const aabb = getRotatedAABB(selectedObject);
    
    // AABB의 좌상단 좌표를 기준으로 표시 (정수 스냅)
    let displayX = Math.round(aabb.minX);
    let displayY = Math.round(aabb.minY);
    
    // 아트보드 기준 상대 좌표 계산
    if (selectedObject.artboardId) {
      const artboard = artboardsMap.get(selectedObject.artboardId);
      if (artboard) {
        displayX = Math.round(aabb.minX - artboard.x);
        displayY = Math.round(aabb.minY - artboard.y);
      }
    }
    
    return {
      x: displayX,
      y: displayY,
      width: selectedObject.width,
      height: selectedObject.height,
      rotation: selectedObject.rotation || 0,
      borderRadiusTopLeft: selectedObject.borderRadiusTopLeft || 0,
      borderRadiusTopRight: selectedObject.borderRadiusTopRight || 0,
      borderRadiusBottomLeft: selectedObject.borderRadiusBottomLeft || 0,
      borderRadiusBottomRight: selectedObject.borderRadiusBottomRight || 0,
      // AABB 정보 저장 (입력 시 사용)
      aabbMinX: aabb.minX,
      aabbMinY: aabb.minY
    };
  }, [
    selectedObject?.id,
    selectedObject?.x,
    selectedObject?.y,
    selectedObject?.width,
    selectedObject?.height,
    selectedObject?.rotation,
    selectedObject?.artboardId,
    selectedObject?.borderRadiusTopLeft,
    selectedObject?.borderRadiusTopRight,
    selectedObject?.borderRadiusBottomLeft,
    selectedObject?.borderRadiusBottomRight,
    artboardsMap
  ]);
  
  React.useEffect(() => {
    if (selectedObjectValues) {
      // 값이 실제로 변경되었는지 확인 (불필요한 업데이트 방지)
      const prev = prevSelectedObjectValuesRef.current;
      const hasChanged = !prev || 
        prev.x !== selectedObjectValues.x ||
        prev.y !== selectedObjectValues.y ||
        prev.width !== selectedObjectValues.width ||
        prev.height !== selectedObjectValues.height ||
        prev.rotation !== selectedObjectValues.rotation ||
        prev.borderRadiusTopLeft !== selectedObjectValues.borderRadiusTopLeft ||
        prev.borderRadiusTopRight !== selectedObjectValues.borderRadiusTopRight ||
        prev.borderRadiusBottomLeft !== selectedObjectValues.borderRadiusBottomLeft ||
        prev.borderRadiusBottomRight !== selectedObjectValues.borderRadiusBottomRight;
      
      if (hasChanged) {
      // 회전값은 도 단위로 변환 후 정수로 반올림 (부동소수점 오차 방지)
      const rotationDeg = selectedObjectValues.rotation * (180 / Math.PI);
      const rotationRounded = Math.round(rotationDeg); // 정수로 반올림 (1도 스냅이므로)
      
        // 포커스가 있는 필드는 업데이트하지 않음 (입력 중 덮어쓰기 방지)
        setInputValues(prev => ({
          x: focusedInputRef.current === 'x' ? prev.x : String(selectedObjectValues.x),
          y: focusedInputRef.current === 'y' ? prev.y : String(selectedObjectValues.y),
          width: focusedInputRef.current === 'width' ? prev.width : String(selectedObjectValues.width),
          height: focusedInputRef.current === 'height' ? prev.height : String(selectedObjectValues.height),
          rotation: focusedInputRef.current === 'rotation' ? prev.rotation : String(rotationRounded),
          borderRadiusTopLeft: focusedInputRef.current === 'borderRadiusTopLeft' ? prev.borderRadiusTopLeft : String(selectedObjectValues.borderRadiusTopLeft),
          borderRadiusTopRight: focusedInputRef.current === 'borderRadiusTopRight' ? prev.borderRadiusTopRight : String(selectedObjectValues.borderRadiusTopRight),
          borderRadiusBottomLeft: focusedInputRef.current === 'borderRadiusBottomLeft' ? prev.borderRadiusBottomLeft : String(selectedObjectValues.borderRadiusBottomLeft),
          borderRadiusBottomRight: focusedInputRef.current === 'borderRadiusBottomRight' ? prev.borderRadiusBottomRight : String(selectedObjectValues.borderRadiusBottomRight)
        }));
        
        // 이전 값 저장
        prevSelectedObjectValuesRef.current = { ...selectedObjectValues };
      }
    } else {
      // 선택된 객체가 없을 때는 포커스 상태와 관계없이 모두 초기화
      setInputValues({ 
        x: '', y: '', width: '', height: '', rotation: '',
        borderRadiusTopLeft: '', borderRadiusTopRight: '',
        borderRadiusBottomLeft: '', borderRadiusBottomRight: ''
      });
      prevSelectedObjectValuesRef.current = null;
      focusedInputRef.current = null;
    }
    // 선택된 객체가 변경될 때 테두리 두께 임시 입력값 초기화 (포커스가 없을 때만)
    if (focusedInputRef.current !== 'borderWidth') {
    setTempBorderWidth('');
    }
    // 아웃글로우 임시 입력값 초기화 (포커스가 없을 때만)
    if (focusedInputRef.current !== 'outGlowOpacity') {
      setTempOutGlowOpacity('');
    }
    if (focusedInputRef.current !== 'outGlowSize') {
      setTempOutGlowSize('');
    }
    if (focusedInputRef.current !== 'outGlowSpread') {
      setTempOutGlowSpread('');
    }
    // 블러 임시 입력값 초기화 (포커스가 없을 때만)
    if (focusedInputRef.current !== 'blurRadius') {
      setTempBlurRadius('');
    }
    // 폰트 크기 임시 입력값 초기화 (포커스가 없을 때만)
    if (focusedInputRef.current !== 'fontSize') {
      setTempFontSize('');
    }
  }, [selectedObjectValues]); // 객체의 실제 값이 변경될 때만 동기화

  // BorderWidth 값 동기화 (포커스 추적 포함)
  React.useEffect(() => {
    const prev = prevSelectedBorderWidthRef.current;
    const hasChanged = prev === null || prev !== selectedBorderWidth;
    
    if (hasChanged) {
      if (focusedInputRef.current !== 'borderWidth') {
        if (tempBorderWidth !== '' && tempBorderWidth !== String(selectedBorderWidth)) {
          setTempBorderWidth('');
        }
      }
      prevSelectedBorderWidthRef.current = selectedBorderWidth;
    }
  }, [selectedBorderWidth, tempBorderWidth]);
  
  // 선택된 객체가 변경될 때 테두리 두께 임시 입력값 초기화
  React.useEffect(() => {
    setTempBorderWidth('');
    prevSelectedBorderWidthRef.current = null;
  }, [selectedObject?.id]);
  
  // OutGlowOpacity 값 동기화 (포커스 추적 포함)
  React.useEffect(() => {
    const prev = prevSelectedOutGlowOpacityRef.current;
    const hasChanged = prev === null || prev !== selectedOutGlowOpacity;
    
    if (hasChanged) {
      if (focusedInputRef.current !== 'outGlowOpacity') {
        if (tempOutGlowOpacity !== '' && tempOutGlowOpacity !== String(selectedOutGlowOpacity)) {
          setTempOutGlowOpacity('');
        }
      }
      prevSelectedOutGlowOpacityRef.current = selectedOutGlowOpacity;
    }
  }, [selectedOutGlowOpacity, tempOutGlowOpacity]);
  
  // OutGlowSize 값 동기화 (포커스 추적 포함)
  React.useEffect(() => {
    const prev = prevSelectedOutGlowSizeRef.current;
    const hasChanged = prev === null || prev !== selectedOutGlowSize;
    
    if (hasChanged) {
      if (focusedInputRef.current !== 'outGlowSize') {
        if (tempOutGlowSize !== '' && tempOutGlowSize !== String(selectedOutGlowSize)) {
          setTempOutGlowSize('');
        }
      }
      prevSelectedOutGlowSizeRef.current = selectedOutGlowSize;
    }
  }, [selectedOutGlowSize, tempOutGlowSize]);
  
  // OutGlowSpread 값 동기화 (포커스 추적 포함)
  React.useEffect(() => {
    const prev = prevSelectedOutGlowSpreadRef.current;
    const hasChanged = prev === null || prev !== selectedOutGlowSpread;
    
    if (hasChanged) {
      if (focusedInputRef.current !== 'outGlowSpread') {
        if (tempOutGlowSpread !== '' && tempOutGlowSpread !== String(selectedOutGlowSpread)) {
          setTempOutGlowSpread('');
        }
      }
      prevSelectedOutGlowSpreadRef.current = selectedOutGlowSpread;
    }
  }, [selectedOutGlowSpread, tempOutGlowSpread]);
  
  // 선택된 객체가 변경될 때 아웃글로우 임시 입력값 초기화
  React.useEffect(() => {
    setTempOutGlowOpacity('');
    setTempOutGlowSize('');
    setTempOutGlowSpread('');
    prevSelectedOutGlowOpacityRef.current = null;
    prevSelectedOutGlowSizeRef.current = null;
    prevSelectedOutGlowSpreadRef.current = null;
  }, [selectedObject?.id]);

  // BlurRadius 값 동기화 (포커스 추적 포함)
  React.useEffect(() => {
    const prev = prevSelectedBlurRadiusRef.current;
    const hasChanged = prev === null || prev !== selectedBlurRadius;
    
    if (hasChanged) {
      if (focusedInputRef.current !== 'blurRadius') {
        if (tempBlurRadius !== '' && tempBlurRadius !== String(selectedBlurRadius)) {
          setTempBlurRadius('');
        }
      }
      prevSelectedBlurRadiusRef.current = selectedBlurRadius;
    }
  }, [selectedBlurRadius, tempBlurRadius]);
  
  // 선택된 객체가 변경될 때 블러 임시 입력값 초기화
  React.useEffect(() => {
    setTempBlurRadius('');
    prevSelectedBlurRadiusRef.current = null;
  }, [selectedObject?.id]);

  // 툴 전환 시 선택 해제 (select와 text가 아닐 때)
  React.useEffect(() => {
    if (activeTool !== 'select' && activeTool !== 'text' && selectedId) {
      setSelectedId(null);
      clearSelection();
      requestRender();
    }
  }, [activeTool, selectedId, setSelectedId, clearSelection, requestRender]);

  // 초기 상태 저장 (마운트 시 한 번만)
  React.useEffect(() => {
    if (!hasInitialStateSavedRef.current && objects.size === 0 && zOrder.length === 0) {
      hasInitialStateSavedRef.current = true;
      saveToHistory({
        objects: new Map(objects),
        zOrder: [...zOrder],
        selectedId
      });
      prevObjectsRef.current = objects;
      prevZOrderRef.current = zOrder;
      prevSelectedIdRef.current = selectedId;
    }
  }, [objects.size, zOrder.length, selectedId, saveToHistory]);

  // 히스토리 저장 콜백 (마우스 작업 완료 시 호출)
  const handleWorkComplete = React.useCallback(() => {
    if (isRestoringHistoryRef.current) return;
    if (!hasInitialStateSavedRef.current) return;
    
    // 객체 이동/리사이즈/회전 완료 시 아트보드 자동 할당
    updateArtboardAssignments();
    
    saveToHistory({
      objects: new Map(objects),
      zOrder: [...zOrder],
      selectedId
    });
  }, [objects, zOrder, selectedId, saveToHistory, updateArtboardAssignments]);

  // 히스토리 저장: 마우스 작업 외의 변경 사항 처리 (속성 변경, 삭제, 추가 등)
  React.useEffect(() => {
    // undo/redo 중이면 스킵
    if (isRestoringHistoryRef.current) {
      isRestoringHistoryRef.current = false;
      // ref 업데이트
      prevObjectsRef.current = objects;
      prevZOrderRef.current = zOrder;
      prevSelectedIdRef.current = selectedId;
      return;
    }
    
    // 초기 상태 저장 전이면 스킵
    if (!hasInitialStateSavedRef.current) {
      return;
    }
    
    // 마우스 작업 중이면 스킵 (드래그, 리사이즈, 회전, 컬러피커 드래그는 완료 시점에서 처리)
    const isWorking = isDraggingRef.current || isResizingRef.current || isRotatingRef.current || isDrawingRef.current || isChangingColorRef.current;
    
    // 실제로 변경이 있었는지 확인
    const objectsChanged = prevObjectsRef.current !== objects;
    const zOrderChanged = prevZOrderRef.current !== zOrder;
    
    // 컬러피커 드래그 중이면 prevObjectsRef만 업데이트하고 히스토리 저장 스킵
    if (isChangingColorRef.current) {
      if (objectsChanged || zOrderChanged) {
        prevObjectsRef.current = objects;
        prevZOrderRef.current = zOrder;
        prevSelectedIdRef.current = selectedId;
      }
      return;
    }
    
    if (isWorking) {
      return;
    }
    
    // 마우스 작업 없이 직접 변경된 경우만 히스토리 저장 (속성 변경, 삭제, 추가 등)
    if (objectsChanged || zOrderChanged) {
      saveToHistory({
        objects: new Map(objects),
        zOrder: [...zOrder],
        selectedId
      });
    }
    
    // 이전 상태 업데이트
    prevObjectsRef.current = objects;
    prevZOrderRef.current = zOrder;
    prevSelectedIdRef.current = selectedId;
  }, [objects, zOrder, selectedId, saveToHistory]);

  // 텍스트 일괄 변경 적용
  const applyTextReplace = React.useCallback(() => {
    const mappings = Object.entries(textReplaceMap).filter(
      ([oldText, newText]) => oldText !== newText
    );
    
    if (mappings.length === 0) {
      setIsTextReplaceOpen(false);
      return;
    }
    
    const measureCanvas = document.createElement('canvas');
    const measureCtx = measureCanvas.getContext('2d');
    
    // Map 기반 업데이트
    mappings.forEach(([oldText, newText]) => {
      objects.forEach((obj, id) => {
        if (obj.type === 'text' && obj.text === oldText) {
          // 텍스트 크기 재계산
          if (!measureCtx) {
            updateObject(id, (o) => ({ ...o, text: newText }));
            return;
          }
          
          const fontSize = obj.fontSize || 16;
          const fontFamily = obj.fontFamily || 'PretendardJP';
          const fontStyle = obj.fontStyle || 'normal';
          const fontWeight = obj.fontWeight || 'normal';
          
          // 폰트 패밀리 이름을 항상 따옴표로 감싸기
          const quotedFontFamily = `"${fontFamily}"`;
          measureCtx.font = `${fontStyle} ${fontWeight} ${fontSize}px ${quotedFontFamily}`;
          
          const lines = newText.split('\n');
          let maxWidth = 0;
          lines.forEach(line => {
            const w = measureCtx.measureText(line || ' ').width;
            if (w > maxWidth) maxWidth = w;
          });
          
          const textWidth = Math.round(Math.max(maxWidth, 100));
          const textHeight = Math.round(fontSize * 1.2 * lines.length);
          
          const align = obj.textAlign || 'left';
          let newX = obj.x;
          if (align === 'center') {
            newX = obj.x + (obj.width - textWidth) / 2;
          } else if (align === 'right') {
            newX = obj.x + (obj.width - textWidth);
          }
          
          updateObject(id, (o) => ({
            ...o,
            text: newText,
            x: Math.round(newX),
            width: textWidth,
            height: textHeight
          }));
        }
      });
    });
    
    // 히스토리 저장 (useEffect가 자동으로 처리하지만, 명시적으로 저장)
    saveToHistory({
      objects: new Map(objects),
      zOrder: [...zOrder],
      selectedId
    });
    
    requestRender();
    setIsTextReplaceOpen(false);
  }, [textReplaceMap, objects, zOrder, selectedId, updateObject, requestRender, saveToHistory]);

  // Undo 핸들러
  const handleUndo = React.useCallback(() => {
    if (!canUndo) return;
    
    isRestoringHistoryRef.current = true;
    const historyState = undo();
    
    if (historyState) {
      // 이미지 객체 복원 시 imageHash로 레지스트리에서 재조회
      const restoredObjects = new Map<string, CanvasObject>(historyState.objects);
      Array.from(restoredObjects.entries()).forEach(([id, obj]) => {
        if (obj.type === 'image' && obj.imageHash && !obj.image) {
          const entry = imageRegistry.getImage(obj.imageHash);
          if (entry) {
            restoredObjects.set(id, {
              ...obj,
              image: entry.image,
              imageSrc: entry.src
            });
          }
        }
      });
      
      restoreState({
        objects: restoredObjects,
        zOrder: historyState.zOrder,
        selectedId: historyState.selectedId
      });
      
      requestRender();
    }
  }, [canUndo, undo, restoreState, requestRender, imageRegistry]);

  // Redo 핸들러
  const handleRedo = React.useCallback(() => {
    if (!canRedo) return;
    
    isRestoringHistoryRef.current = true;
    const historyState = redo();
    
    if (historyState) {
      // 이미지 객체 복원 시 imageHash로 레지스트리에서 재조회
      const restoredObjects = new Map<string, CanvasObject>(historyState.objects);
      Array.from(restoredObjects.entries()).forEach(([id, obj]) => {
        if (obj.type === 'image' && obj.imageHash && !obj.image) {
          const entry = imageRegistry.getImage(obj.imageHash);
          if (entry) {
            restoredObjects.set(id, {
              ...obj,
              image: entry.image,
              imageSrc: entry.src
            });
          }
        }
      });
      
      restoreState({
        objects: restoredObjects,
        zOrder: historyState.zOrder,
        selectedId: historyState.selectedId
      });
      
      requestRender();
    }
  }, [canRedo, redo, restoreState, requestRender, imageRegistry]);

  // 선택 리사이징
  useResizeSelected(
    canvasRef,
    () => canvasState,
    () => activeTool,
    selectedId,
    () => {
      const selectedObjs = getSelectedObjects();
      if (selectedObjs.length === 0) return null;
      // 다중 선택 시 회전된 객체의 실제 AABB 포함
      const isMultiSelect = selectedIds && selectedIds.size > 1;
      if (isMultiSelect) {
        return computeRotatedAABB(selectedObjs);
      }
      return computeAABB(selectedObjs);
    },
    resizeSelected,
    requestRender,
    () => getIsPanning(),
    getObjectById,
    updateObject,
    isResizingRef,
    handleWorkComplete,
    selectedIds,
    getSelectedObjects
  );

  // 선택 회전
  useRotateSelected(
    canvasRef,
    () => canvasState,
    () => activeTool,
    selectedId,
    getObjectById,
    updateObject,
    requestRender,
    () => getIsPanning(),
    isRotatingRef,
    handleWorkComplete,
    selectedIds,
    getSelectedObjects,
    () => {
      const selectedObjs = getSelectedObjects();
      if (selectedObjs.length === 0) return null;
      // 다중 선택 시 회전된 객체의 실제 AABB 포함
      const isMultiSelect = selectedIds && selectedIds.size > 1;
      if (isMultiSelect) {
        return computeRotatedAABB(selectedObjs);
      }
      return computeAABB(selectedObjs);
    },
    rotateSelectedBy
  );

  // 클립보드 관리 (복사/붙여넣기/오려두기)
  const { handleCopy, handlePaste, handleCut } = useClipboard({
    selectedId,
    getObjectById,
    addObject,
    setSelectedId,
    imageRegistry,
    requestRender,
    selectedIds,
    getSelectedObjects,
    setSelection,
    zOrder,
    setZOrder,
    setObjects,
    deleteObject,
    clearSelection,
    clipMaskMap
  });

  // 키보드 단축키
  useKeyboard({
    onDelete: () => {
      // 다중 선택 모드 체크
      if (selectedIds && selectedIds.size > 1) {
        // 다중 선택: 모든 선택된 객체 삭제
        const idsToDelete = Array.from(selectedIds);
        idsToDelete.forEach(id => {
          deleteObject(id);
        });
        // 선택 해제
        clearSelection();
        setSelectedId(null);
      } else if (selectedId) {
        // 단일 선택: 기존 로직
        deleteObject(selectedId);
      }
      requestRender();
    },
    onCopy: handleCopy,
    onCut: handleCut,
    onPaste: handlePaste,
    onUndo: handleUndo,
    onRedo: handleRedo,
    onEscape: () => {
      // ESC 키: 선택 해제
      if (selectedIds && selectedIds.size > 0) {
        clearSelection();
        setSelectedId(null);
        requestRender();
      } else if (selectedId) {
        setSelectedId(null);
        requestRender();
      }
    },
    onToggleClipMask: () => {
      if (selectedId) {
        toggleClipMask([selectedId]);
        requestRender();
      }
    },
    onToggleLayerLock: () => {
      if (!selectedId) return;
      const target = getObjectById(selectedId);
      if (!target || target.type !== 'image') return;
      updateObject(selectedId, (obj) => ({
        ...obj,
        isLocked: !obj.isLocked
      }));
      saveToHistory({ objects, zOrder, selectedId });
      requestRender();
    },
    onArrowKey: (dx, dy) => {
      // 방향키로 객체 이동
      if (selectedIds && selectedIds.size > 0) {
        // 다중 선택: 모든 선택된 객체 이동
        const idsToMove = Array.from(selectedIds);
        moveSelectedBy(dx, dy, idsToMove);
      } else if (selectedId) {
        // 단일 선택: 선택된 객체 이동
        moveSelectedBy(dx, dy);
      }
      requestRender();
    },
    onMoveLayerUp: () => {
      const didMove = moveSelectedInZOrderByShortcut('up');
      if (!didMove) return;
      saveToHistory({ objects, zOrder, selectedId });
      requestRender();
    },
    onMoveLayerDown: () => {
      const didMove = moveSelectedInZOrderByShortcut('down');
      if (!didMove) return;
      saveToHistory({ objects, zOrder, selectedId });
      requestRender();
    },
    onMoveLayerToFront: () => {
      const didMove = moveSelectedInZOrderByShortcut('up', 'extreme');
      if (!didMove) return;
      saveToHistory({ objects, zOrder, selectedId });
      requestRender();
    },
    onMoveLayerToBack: () => {
      const didMove = moveSelectedInZOrderByShortcut('down', 'extreme');
      if (!didMove) return;
      saveToHistory({ objects, zOrder, selectedId });
      requestRender();
    },
    onToolChange: (tool) => {
      setActiveTool(tool);
      requestRender();
    },
    onNewProject: handleNewProject,
    onExportThumbnail: () => {
      // Ctrl+Shift+Alt+S: 썸네일 저장 (60% 해상도, JPG 75% 품질)
      exportArtboardAsImage(
        selectedArtboardId,
        artboards,
        getObjectsArray,
        'jpeg',
        0.75,  // JPG 품질 75%
        '',
        0.6,   // 60% 스케일
        true   // 썸네일 플래그 (thumb_ 접두사 추가)
      );
    },
    onExportPNG: () => {
      // Ctrl+Shift+S: PNG 저장 (원본 해상도)
      const selectedObjs = getSelectedObjects();
      let selectedArtboardId: string | null = null;
      
      // 선택된 객체가 있고 모두 같은 아트보드에 속해있으면 해당 아트보드 저장
      if (selectedObjs.length > 0) {
        const firstArtboardId = selectedObjs[0].artboardId;
        const allSameArtboard = selectedObjs.every(obj => obj.artboardId === firstArtboardId);
        if (allSameArtboard && firstArtboardId) {
          selectedArtboardId = firstArtboardId;
        }
      }
      
      exportArtboardAsImage(
        selectedArtboardId,
        initialArtboards,
        getObjectsArray,
        'png',
        1.0,
        projectName
      ).catch((error) => {
        console.error('PNG 저장 실패:', error);
      });
    },
    onLoadProject: handleLoadProject,
    onSaveProject: handleSaveProject
  });

  // 사용자 정의 드로우: 아트보드/객체/임시 사각형 레이어 분리 호출
  React.useEffect(() => {
    setCustomDraw((ctx, state) => {
      renderArtboards(ctx, state, initialArtboards, selectedArtboardId);
      const list = getObjectsArray();
      const isInteracting = isDraggingRef.current || isResizingRef.current || isRotatingRef.current;
      // 이동 중인 객체 ID Set 생성 (블러 효과 최적화용)
      const interactingObjectIds = isInteracting && (selectedId || (selectedIds && selectedIds.size > 0))
        ? new Set(selectedIds && selectedIds.size > 0 ? Array.from(selectedIds) : (selectedId ? [selectedId] : []))
        : undefined;
      renderObjects(ctx, state, list, clipMaskMap, isInteracting, artboardsMap, interactingObjectIds);
      // 임시 사각형
      if (tempRectRef.current) {
        renderTempRect(ctx, state, tempRectRef.current);
      }
      // 오버레이(선택)
      const selectedObjs = getSelectedObjects();
      const isMultiSelect = selectedIds && selectedIds.size > 1;
      let bbox: AABB | null = null;
      let rot = 0;
      
      if (isMultiSelect && selectedObjs.length > 0) {
        // 다중 선택: 회전을 고려한 실제 바운딩 박스, 회전 0도
        bbox = computeRotatedAABB(selectedObjs);
        rot = 0;
      } else if (selectedId && selectedObjs.length > 0) {
        // 단일 선택: 기존 로직 (회전 고려 안 함, renderOverlay에서 회전 적용)
        bbox = computeAABB(selectedObjs);
        rot = selectedObjs[0]?.rotation || 0;
      }
      
      renderOverlay(ctx, state, bbox, rot);
      
      // 드래그 선택 영역 렌더링
      if (dragSelectRect && dragSelectRect.width > 0 && dragSelectRect.height > 0) {
        ctx.save();
        ctx.strokeStyle = '#007bff';
        ctx.lineWidth = 1 / state.scale;
        ctx.setLineDash([5 / state.scale, 5 / state.scale]);
        ctx.strokeRect(dragSelectRect.x, dragSelectRect.y, dragSelectRect.width, dragSelectRect.height);
        ctx.fillStyle = 'rgba(0, 123, 255, 0.1)';
        ctx.fillRect(dragSelectRect.x, dragSelectRect.y, dragSelectRect.width, dragSelectRect.height);
        ctx.restore();
      }
      
      // 드래그 오버 하이라이트 (이미지)
      if (dragOverImageId || dragOverImageHash) {
        list.forEach(obj => {
          if (obj.type === 'image' && !obj.isLocked) {
            const isSingleHighlight = dragOverImageId === obj.id;
            const isHashHighlight = dragOverImageHash && obj.imageHash && dragOverImageHash === obj.imageHash;
            if (isSingleHighlight || isHashHighlight) {
              const rot = obj.rotation || 0;
              const hasRot = Math.abs(rot) > 1e-6;
              
              // 회전 적용
              if (hasRot) {
                ctx.save();
                const cx = obj.x + obj.width / 2;
                const cy = obj.y + obj.height / 2;
                ctx.translate(cx, cy);
                ctx.rotate(rot);
                ctx.translate(-cx, -cy);
              }
              
              // 반투명 채우기
              ctx.save();
              ctx.globalAlpha = 0.3;
              ctx.fillStyle = isHashHighlight ? '#e83e8c' : '#007bff';
              ctx.fillRect(obj.x, obj.y, obj.width, obj.height);
              ctx.restore();
              
              // 테두리 하이라이트
              ctx.save();
              ctx.strokeStyle = isHashHighlight ? '#e83e8c' : '#007bff';
              ctx.lineWidth = 3 / state.scale;
              ctx.strokeRect(obj.x, obj.y, obj.width, obj.height);
              ctx.restore();
              
              // 회전 해제
              if (hasRot) {
                ctx.restore();
              }
            }
          }
        });
      }
      
      // ZIP 파일 드래그 시 아트보드 하이라이트 (주황색)
      if (dragOverArtboardId) {
        const artboard = artboardsMap.get(dragOverArtboardId);
        if (artboard) {
          // 반투명 채우기
          ctx.save();
          ctx.globalAlpha = 0.3;
          ctx.fillStyle = '#ff8800';
          ctx.fillRect(artboard.x, artboard.y, artboard.width, artboard.height);
          ctx.restore();
          
          // 테두리 하이라이트
          ctx.save();
          ctx.strokeStyle = '#ff8800';
          ctx.lineWidth = 3 / state.scale;
          ctx.strokeRect(artboard.x, artboard.y, artboard.width, artboard.height);
          ctx.restore();
        }
      }
    });
  }, [setCustomDraw, initialArtboards, getObjectsArray, tempRectRef, selectedId, selectedIds, getSelectedObjects, dragSelectRect, dragOverImageId, dragOverImageHash, dragOverArtboardId, artboardsMap, clipMaskMap]);

  // 툴 전환 시 즉시 렌더 트리거
  React.useEffect(() => {
    requestRender();
  }, [requestRender, activeTool]);

  // 텍스트 툴 클릭 핸들러 (텍스트 툴 또는 더블클릭 편집)
  const handleTextToolClick = React.useCallback((clientX: number, clientY: number, editingId?: string) => {
    if (editingId) {
      // 기존 텍스트 편집 (텍스트 툴 클릭 또는 더블클릭)
      const textObj = getObjectById(editingId);
      if (textObj && textObj.type === 'text') {
        setTextInputMode({ x: clientX, y: clientY, editingId });
        setTextInputValue(textObj.text || '');
        // 포커스를 위해 약간의 지연
        setTimeout(() => {
          textInputRef.current?.focus();
          textInputRef.current?.select();
        }, 0);
      }
    } else {
      // 새 텍스트 생성 (텍스트 툴만)
      if (activeTool !== 'text') return;
      
      setTextInputMode({ x: clientX, y: clientY });
      setTextInputValue('');
      // 포커스를 위해 약간의 지연
      setTimeout(() => {
        textInputRef.current?.focus();
      }, 0);
    }
  }, [activeTool, getObjectById]);

  useMoveSelected(
    canvasRef,
    () => canvasState,
    () => activeTool,
    selectedId,
    getObjectById,
    hitTestPoint,
    moveSelectedBy,
    requestRender,
    () => {
      const selectedObjs = getSelectedObjects();
      if (selectedObjs.length === 0) return null;
      // 다중 선택 시 회전된 객체의 실제 AABB 포함
      const isMultiSelect = selectedIds && selectedIds.size > 1;
      if (isMultiSelect) {
        return computeRotatedAABB(selectedObjs);
      }
      return computeAABB(selectedObjs);
    },
    setSelectedId,
    () => getIsPanning(),
    isDraggingRef,
    handleWorkComplete,
    selectedIds,
    addToSelection,
    removeFromSelection,
    setSelection,
    clearSelection,
    getSelectedObjects,
    getObjectsArray,
    setDragSelectRect,
    handleTextToolClick,
    initialArtboards,
    (artboardId) => {
      setSelectedArtboardId(artboardId);
      // 아트보드 선택 시 객체 선택 해제
      setSelectedId(null);
      clearSelection();
      requestRender();
    }
  );

  // 텍스트 입력 완료 핸들러
  const handleTextInputComplete = React.useCallback(() => {
    if (!textInputMode) {
      setTextInputMode(null);
      setTextInputValue('');
      return;
    }
    
    // 편집 중인 기존 텍스트가 있는지 확인
    const existingTextObject = textInputMode.editingId 
      ? getObjectById(textInputMode.editingId)
      : null;
    
    if (existingTextObject && existingTextObject.type === 'text') {
      // 기존 텍스트 업데이트
      const measureCanvas = document.createElement('canvas');
      const measureCtx = measureCanvas.getContext('2d');
      if (measureCtx) {
        const fontSize = existingTextObject.fontSize || 16;
        const fontFamily = existingTextObject.fontFamily || 'PretendardJP';
        const fontStyle = existingTextObject.fontStyle || 'normal';
        const fontWeight = existingTextObject.fontWeight || 'normal';
        
        // 폰트 패밀리 이름을 항상 따옴표로 감싸기
        const quotedFontFamily = `"${fontFamily}"`;
        measureCtx.font = `${fontStyle} ${fontWeight} ${fontSize}px ${quotedFontFamily}`;
        
        // 여러 줄 텍스트 크기 측정
        const lines = textInputValue.split('\n');
        let maxWidth = 0;
        lines.forEach(line => {
          const width = measureCtx.measureText(line || ' ').width;
          if (width > maxWidth) maxWidth = width;
        });
        
        const textWidth = Math.round(Math.max(maxWidth, 100));
        const textHeight = Math.round(fontSize * 1.2 * (lines.length || 1));
        
        const align = existingTextObject.textAlign || 'left';
        let newX = existingTextObject.x;
        if (align === 'center') newX = existingTextObject.x + (existingTextObject.width - textWidth) / 2;
        else if (align === 'right') newX = existingTextObject.x + (existingTextObject.width - textWidth);
        
        updateObject(existingTextObject.id, (obj) => ({
          ...obj,
          text: textInputValue,
          x: Math.round(newX),
          width: textWidth,
          height: textHeight
        }));
      }
      
      setTextInputMode(null);
      setTextInputValue('');
      saveToHistory({ objects, zOrder, selectedId });
      requestRender();
      return;
    }
    
    // 새 텍스트 생성 (빈 텍스트는 생성하지 않음)
    if (!textInputValue.trim()) {
      setTextInputMode(null);
      setTextInputValue('');
      return;
    }
    
    const canvas = canvasRef.current;
    if (!canvas) {
      setTextInputMode(null);
      setTextInputValue('');
      return;
    }
    
    const canvasRect = canvas.getBoundingClientRect();
    
    // 화면 좌표를 캔버스 좌표로 변환
    const canvasX = (textInputMode.x - canvasRect.left - canvasState.offset.x) / canvasState.scale;
    const canvasY = (textInputMode.y - canvasRect.top - canvasState.offset.y) / canvasState.scale;
    
    // 텍스트 크기 측정
    const fontSize = 50;
    const fontFamily = 'PretendardJP';
    const measureCanvas = document.createElement('canvas');
    const measureCtx = measureCanvas.getContext('2d');
    if (measureCtx) {
      // 폰트 패밀리 이름을 항상 따옴표로 감싸기
      const quotedFontFamily = `"${fontFamily}"`;
      measureCtx.font = `${fontSize}px ${quotedFontFamily}`;
      
      // 여러 줄 텍스트 크기 측정
      const lines = textInputValue.split('\n');
      let maxWidth = 0;
      lines.forEach(line => {
        const width = measureCtx.measureText(line || ' ').width;
        if (width > maxWidth) maxWidth = width;
      });
      
      const textWidth = Math.round(Math.max(maxWidth, 100));
      const textHeight = Math.round(fontSize * 1.2 * (lines.length || 1));
      
      // 텍스트 객체 생성
      const newTextObject: CanvasObject = {
        id: `text_${Date.now()}_${Math.floor(Math.random() * 1000)}`,
        type: 'text',
        x: Math.round(canvasX),
        y: Math.round(canvasY),
        width: textWidth,
        height: textHeight,
        rotation: 0,
        text: textInputValue,
        fontSize,
        fontFamily: 'PretendardJP',
        fontWeight: 'normal',
        fontStyle: 'normal',
        color: defaultColor,
        textAlign: 'left'
      };
      
      addObject(newTextObject);
      // 생성된 텍스트 객체를 자동으로 선택
      setSelectedId(newTextObject.id);
      clearSelection();
      setTextInputMode(null);
      setTextInputValue('');
      saveToHistory({ objects, zOrder, selectedId });
      requestRender();
    } else {
      setTextInputMode(null);
      setTextInputValue('');
    }
  }, [textInputMode, textInputValue, canvasRef, canvasState, objects, zOrder, selectedId, getObjectById, updateObject, addObject, saveToHistory, requestRender, setSelectedId, clearSelection, defaultColor]);

  // 텍스트 입력 취소 핸들러
  const handleTextInputCancel = React.useCallback(() => {
    setTextInputMode(null);
    setTextInputValue('');
  }, []);

  // 이미지 드롭 핸들러
  const handleDropImage = React.useCallback(async (
    file: File,
    e: React.DragEvent,
    mousePos: { x: number; y: number }
  ) => {
    // 지원되는 이미지 타입 확인
    const supportedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp', 'image/svg+xml'];
    if (!supportedTypes.includes(file.type)) {
      alert('지원되지 않는 이미지 형식입니다.');
      return;
    }

    const isCtrlPressed = e.ctrlKey || e.metaKey;
    const isAltPressed = e.altKey;

    // Alt 키가 눌려있을 때만 교체 모드
    if (isAltPressed) {
      // 마우스 위치의 "가장 위 이미지"만 대상으로 히트테스트 (도형/텍스트 무시)
      const hit = hitTestImageAtPoint(mousePos.x, mousePos.y, {
        objects: getObjectsArray(),
        objectsMap: objects,
        artboardsMap: artboardsMap,
        canvasState,
        canvas: canvasRef.current
      });

      const targetObject = hit ? getObjectById(hit.id) : null;

      // 마우스 위치에 이미지가 없으면 아무것도 하지 않음
      if (!targetObject || targetObject.type !== 'image') {
        return;
      }

      try {
        // 파일 해시 계산
        const imageHash = await computeFileHash(file);
        
        // 레지스트리에서 기존 이미지 확인
        const existing = imageRegistry.getImage(imageHash);
        
        if (existing) {
          // 기존 이미지 재사용
          if (isCtrlPressed && isAltPressed && targetObject.imageHash) {
            // Ctrl+Alt: 동일 해시 전체 대체
            const targetHash = targetObject.imageHash;
            const list = getObjectsArray();
            list.forEach(obj => {
              if (obj.type === 'image' && !obj.isLocked && obj.imageHash === targetHash) {
                const naturalW = existing.image.width;
                const naturalH = existing.image.height;
                const scale = Math.min(obj.width / naturalW, obj.height / naturalH);
                const scaledW = Math.round(naturalW * scale);
                const scaledH = Math.round(naturalH * scale);
                const cx = obj.x + obj.width / 2;
                const cy = obj.y + obj.height / 2;
                updateObject(obj.id, () => ({
                  ...obj,
                  x: Math.round(cx - scaledW / 2),
                  y: Math.round(cy - scaledH / 2),
                  width: scaledW,
                  height: scaledH,
                  image: existing.image,
                  imageSrc: existing.src,
                  imageHash,
                  fileName: file.name
                }));
              }
            });
          } else {
            // Ctrl만: 단일 이미지 대체
            const naturalW = existing.image.width;
            const naturalH = existing.image.height;
            const scale = Math.min(targetObject.width / naturalW, targetObject.height / naturalH);
            const scaledW = Math.round(naturalW * scale);
            const scaledH = Math.round(naturalH * scale);
            const cx = targetObject.x + targetObject.width / 2;
            const cy = targetObject.y + targetObject.height / 2;
            updateObject(targetObject.id, () => ({
              ...targetObject,
              x: Math.round(cx - scaledW / 2),
              y: Math.round(cy - scaledH / 2),
              width: scaledW,
              height: scaledH,
              image: existing.image,
              imageSrc: existing.src,
              imageHash,
              fileName: file.name
            }));
          }
          requestRender();
          setActiveTool('select');
          return;
        }

        // 새 이미지 로드
        const reader = new FileReader();
        reader.onload = async (ev) => {
          const dataUrl = ev.target?.result as string;
          const img = await imageRegistry.loadImage(imageHash, dataUrl, file.name);
          
          if (isCtrlPressed && isAltPressed && targetObject.imageHash) {
            // Ctrl+Alt: 동일 해시 전체 대체
            const targetHash = targetObject.imageHash;
            const list = getObjectsArray();
            list.forEach(obj => {
              if (obj.type === 'image' && !obj.isLocked && obj.imageHash === targetHash) {
                const naturalW = img.width;
                const naturalH = img.height;
                const scale = Math.min(obj.width / naturalW, obj.height / naturalH);
                const scaledW = Math.round(naturalW * scale);
                const scaledH = Math.round(naturalH * scale);
                const cx = obj.x + obj.width / 2;
                const cy = obj.y + obj.height / 2;
                updateObject(obj.id, () => ({
                  ...obj,
                  x: Math.round(cx - scaledW / 2),
                  y: Math.round(cy - scaledH / 2),
                  width: scaledW,
                  height: scaledH,
                  image: img,
                  imageSrc: dataUrl,
                  imageHash,
                  fileName: file.name
                }));
              }
            });
          } else {
            // Ctrl만: 단일 이미지 대체
            const naturalW = img.width;
            const naturalH = img.height;
            const scale = Math.min(targetObject.width / naturalW, targetObject.height / naturalH);
            const scaledW = Math.round(naturalW * scale);
            const scaledH = Math.round(naturalH * scale);
            const cx = targetObject.x + targetObject.width / 2;
            const cy = targetObject.y + targetObject.height / 2;
            updateObject(targetObject.id, () => ({
              ...targetObject,
              x: Math.round(cx - scaledW / 2),
              y: Math.round(cy - scaledH / 2),
              width: scaledW,
              height: scaledH,
              image: img,
              imageSrc: dataUrl,
              imageHash,
              fileName: file.name
            }));
          }
          requestRender();
          setActiveTool('select');
        };
        reader.onerror = () => {
          alert('이미지 로드에 실패했습니다.');
        };
        reader.readAsDataURL(file);
      } catch (error) {
        console.error('이미지 처리 오류:', error);
        alert('이미지 처리 중 오류가 발생했습니다.');
      }
      return;
    }

    // Ctrl 키가 눌려있지 않으면 새 이미지 생성
    try {
      // 파일 해시 계산
      const imageHash = await computeFileHash(file);
      
      // 레지스트리에서 기존 이미지 확인
      const existing = imageRegistry.getImage(imageHash);
      
      if (existing) {
        // 기존 이미지 재사용하여 새 객체 생성
        const id = `image_${Date.now()}_${Math.floor(Math.random() * 1000)}`;
        const imageObject: CanvasObject = {
          id,
          type: 'image',
          x: Math.round(mousePos.x - existing.image.width / 2),
          y: Math.round(mousePos.y - existing.image.height / 2),
          width: existing.image.width,
          height: existing.image.height,
          rotation: 0,
          image: existing.image,
          imageSrc: existing.src,
          imageHash,
          fileName: file.name
        };
        addObject(imageObject);
        requestRender();
        setActiveTool('select');
        return;
      }

      // 새 이미지 로드
      const reader = new FileReader();
      reader.onload = async (ev) => {
        const dataUrl = ev.target?.result as string;
        const img = await imageRegistry.loadImage(imageHash, dataUrl, file.name);
        
        const id = `image_${Date.now()}_${Math.floor(Math.random() * 1000)}`;
        const imageObject: CanvasObject = {
          id,
          type: 'image',
          x: Math.round(mousePos.x - img.width / 2),
          y: Math.round(mousePos.y - img.height / 2),
          width: img.width,
          height: img.height,
          rotation: 0,
          image: img,
          imageSrc: dataUrl,
          imageHash,
          fileName: file.name
        };
        addObject(imageObject);
        requestRender();
        setActiveTool('select');
      };
      reader.onerror = () => {
        alert('이미지 로드에 실패했습니다.');
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('이미지 처리 오류:', error);
      alert('이미지 처리 중 오류가 발생했습니다.');
    }
  }, [imageRegistry, getObjectById, hitTestPoint, updateObject, getObjectsArray, addObject, requestRender, setActiveTool]);

  // 이미지 드래그 앤 드롭
  const { handleDragOver, handleDrop, handleDragLeave } = useImageDragDrop({
    canvasRef,
    getCanvasState: () => canvasState,
    onDropImage: handleDropImage,
    onDragOverImage: setDragOverImageId,
    onDragOverImageHash: setDragOverImageHash,
    onDragOverArtboard: setDragOverArtboardId,
    onDropTemplate: handleLoadTemplateFile,
    getObjectsArray,
    getArtboardsMap: () => artboardsMap,
    getObjectsMap: () => objects
  });

  return (
    <div className="app">
      {/* 불러온 템플릿 이름 라벨 */}
      {loadedTemplateName && (
        <div className="template-name-label">
          <span className="template-name-label__text">{loadedTemplateName}</span>
          <button
            className="template-name-label__copy-button"
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(loadedTemplateName);
                // 복사 성공 피드백 (선택사항)
                const button = document.querySelector('.template-name-label__copy-button') as HTMLElement;
                if (button) {
                  const originalTitle = button.getAttribute('title') || '';
                  button.setAttribute('title', '복사됨!');
                  setTimeout(() => {
                    button.setAttribute('title', originalTitle || '복사');
                  }, 1000);
                }
              } catch (error) {
                console.error('복사 실패:', error);
                alert('복사에 실패했습니다.');
              }
            }}
            title="복사"
            aria-label="템플릿 이름 복사"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M16 1H4C2.9 1 2 1.9 2 3V17H4V3H16V1ZM19 5H8C6.9 5 6 5.9 6 7V21C6 22.1 6.9 23 8 23H19C20.1 23 21 22.1 21 21V7C21 5.9 20.1 5 19 5ZM19 21H8V7H19V21Z" fill="currentColor"/>
            </svg>
          </button>
        </div>
      )}
      {/* 좌측 패널 (레이어/프로젝트/블렌드/투명도) - UI 전용 */}
      <aside className="toolbar">
        <div className="info-panel__section" style={{padding:0}}>
          {/* 새 프로젝트 버튼 */}
          <div style={{ padding: '0px 0px 0px 0px', marginBottom: '8px' }}>
            <button
              className="new-project-button"
              onClick={handleNewProject}
              title="새 프로젝트 시작 (모든 작업 내용 삭제)"
            >
              새 프로젝트
            </button>
          </div>
          <div className="info-field" style={{ padding: '6px 0 6px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <label className="info-panel__label" style={{ margin: 0, whiteSpace: 'nowrap' }}>프로젝트</label>
              <input 
                type="text" 
                className="info-panel__input" 
                placeholder="프로젝트 이름" 
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
              />
            </div>
          </div>
          <BlendModeControl
            getSelectedObjects={getSelectedObjects}
            updateObject={updateObject}
            requestRender={requestRender}
            saveToHistory={saveToHistory}
            objects={objects}
            zOrder={zOrder}
            selectedId={selectedId}
          />
          <OpacityControl
            getSelectedObjects={getSelectedObjects}
            updateObject={updateObject}
            requestRender={requestRender}
            saveToHistory={saveToHistory}
            objects={objects}
            zOrder={zOrder}
            selectedId={selectedId}
          />
        </div>
        <div className="info-panel__content" style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          <LayerList
            zOrder={zOrder}
            objects={objects}
            artboards={artboards}
            filterResetKey={layerFilterResetKey}
            selectedId={selectedId}
            selectedIds={selectedIds}
            draggingLayerId={draggingLayerId}
            dragOverLayerId={dragOverLayerId}
            dropAbove={dropAbove}
            setDraggingLayerId={setDraggingLayerId}
            setDragOverLayerId={setDragOverLayerId}
            setDropAbove={setDropAbove}
            removeFromSelection={removeFromSelection}
            addToSelection={addToSelection}
            setSelection={setSelection}
            moveObjectsInZOrder={moveObjectsInZOrder}
            updateObject={updateObject}
            saveToHistory={saveToHistory}
            requestRender={requestRender}
          />
        </div>
      </aside>
      <div className="canvas-container">
        <div className="zoom-display"><span className="zoom-display__text">{canvasState.zoomPercentage}%</span></div>
        {/* 정렬 툴바 */}
        <AlignToolbar
          selectedObjects={getSelectedObjects()}
          updateObject={updateObject}
          requestRender={requestRender}
          saveToHistory={saveToHistory}
          objects={objects}
          zOrder={zOrder}
          selectedId={selectedId}
          artboardsMap={artboardsMap}
        />
        <CanvasView 
          canvasRef={canvasRef}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onDragLeave={handleDragLeave}
          cursor={activeTool === 'text' ? 'text' : activeTool === 'select' ? 'default' : 'crosshair'}
        />
        {/* 하단 중앙 툴바: 백업본처럼 아이콘형 툴 배치 (disabled) */}
        <div className="bottom-toolbar">
          <button
            className={`bottom-toolbar__button${activeTool==='select' ? ' is-active' : ''}`}
            title="선택"
            onClick={() => { setActiveTool('select'); requestRender(); }}
            aria-pressed={activeTool==='select'}
            style={activeTool==='select' ? { background:'#1e293b', color:'#fff', borderColor:'#0ea5e9' } : undefined}
          >↖</button>
          <button
            className={`bottom-toolbar__button${activeTool==='rectangle' ? ' is-active' : ''}`}
            title="사각형"
            onClick={() => { setActiveTool('rectangle'); requestRender(); }}
            aria-pressed={activeTool==='rectangle'}
            style={activeTool==='rectangle' ? { background:'#1e293b', color:'#fff', borderColor:'#0ea5e9' } : undefined}
          >▭</button>
          <button
            className={`bottom-toolbar__button${activeTool==='circle' ? ' is-active' : ''}`}
            title="원"
            onClick={() => { setActiveTool('circle'); requestRender(); }}
            aria-pressed={activeTool==='circle'}
            style={activeTool==='circle' ? { background:'#1e293b', color:'#fff', borderColor:'#0ea5e9' } : undefined}
          >◯</button>
          <button
            className={`bottom-toolbar__button${activeTool==='text' ? ' is-active' : ''}`}
            title="텍스트"
            onClick={() => { setActiveTool('text'); requestRender(); }}
            aria-pressed={activeTool==='text'}
            style={activeTool==='text' ? { background:'#1e293b', color:'#fff', borderColor:'#0ea5e9' } : undefined}
          >T</button>
          <button 
            className="bottom-toolbar__button bottom-toolbar__button--save" 
            title="텍스트 교체"
            onClick={() => {
              const initial: Record<string, string> = {};
              uniqueTexts.forEach(t => { initial[t] = t; });
              setTextReplaceMap(initial);
              setIsTextReplaceOpen(true);
            }}
            disabled={uniqueTexts.length === 0}
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              gap: '2px',
              opacity: uniqueTexts.length === 0 ? 0.5 : 1
            }}
          >
            <span style={{ fontSize: '14px', fontWeight: 'bold' }}>T</span>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginLeft: '2px' }}>
              <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4C7.58 4 4 7.58 4 12C4 16.42 7.58 20 12 20C15.73 20 18.84 17.45 19.73 14H17.65C16.83 16.33 14.61 18 12 18C8.69 18 6 15.31 6 12C6 8.69 8.69 6 12 6C13.66 6 15.14 6.69 16.22 7.78L13 11H20V4L17.65 6.35Z" fill="currentColor"/>
            </svg>
          </button>
          <button
            className={`bottom-toolbar__button${activeTool==='artboard' ? ' is-active' : ''}`}
            title="아트보드"
            onClick={() => { setActiveTool('artboard'); requestRender(); }}
            aria-pressed={activeTool==='artboard'}
            style={activeTool==='artboard' ? { background:'#1e293b', color:'#fff', borderColor:'#0ea5e9' } : undefined}
          >▦</button>
          <div className="bottom-toolbar__divider"></div>
          <button 
            className="bottom-toolbar__button bottom-toolbar__button--save" 
            title="썸네일 저장 (60%, JPG 75%)"
            onClick={() => {
              const selectedObjs = getSelectedObjects();
              let selectedArtboardId: string | null = null;
              
              // 선택된 객체가 있고 모두 같은 아트보드에 속해있으면 해당 아트보드 저장
              if (selectedObjs.length > 0) {
                const firstArtboardId = selectedObjs[0].artboardId;
                const allSameArtboard = selectedObjs.every(obj => obj.artboardId === firstArtboardId);
                if (allSameArtboard && firstArtboardId) {
                  selectedArtboardId = firstArtboardId;
                }
              }
              
              exportArtboardAsImage(
                selectedArtboardId,
                artboards,
                getObjectsArray,
                'jpeg',
                0.75,  // JPG 품질 75%
                '',
                0.6,   // 60% 스케일
                true   // 썸네일 플래그 (thumb_ 접두사 추가)
              ).catch((error) => {
                console.error('썸네일 저장 실패:', error);
              });
            }}
          >📷</button>
          <button 
            className="bottom-toolbar__button bottom-toolbar__button--save" 
            title="템플릿 갤러리"
            onClick={() => setIsTemplateGalleryOpen(true)}
          >🗃️</button>
          <button 
            className="bottom-toolbar__button bottom-toolbar__button--save" 
            title="불러오기"
            onClick={handleLoadProject}
          >📂</button>
          <button 
            className="bottom-toolbar__button bottom-toolbar__button--save" 
            title="저장"
            onClick={handleSaveProject}
          >💾</button>
          <button 
            className="bottom-toolbar__button bottom-toolbar__button--save" 
            title="PNG 저장"
            onClick={() => {
              const selectedObjs = getSelectedObjects();
              let selectedArtboardId: string | null = null;
              
              // 선택된 객체가 있고 모두 같은 아트보드에 속해있으면 해당 아트보드 저장
              if (selectedObjs.length > 0) {
                const firstArtboardId = selectedObjs[0].artboardId;
                const allSameArtboard = selectedObjs.every(obj => obj.artboardId === firstArtboardId);
                if (allSameArtboard && firstArtboardId) {
                  selectedArtboardId = firstArtboardId;
                }
              }
              
              exportArtboardAsImage(
                selectedArtboardId,
                initialArtboards,
                getObjectsArray,
                'png',
                1.0,
                projectName
              ).catch((error) => {
                console.error('PNG 저장 실패:', error);
              });
            }}
          >🖼️</button>
        </div>
      </div>
      <aside className="info-panel">
        {/* 색상 */}
        <div className="info-panel__section">
          <div className="info-panel__content">
            <div className="color-picker-container">
              <div className="color-swatches">
                <div 
                  className="color-swatch current-color"
                  style={{ 
                    backgroundColor: currentColor
                  }}
                  title="현재 색상"
                />
                {(window as any).EyeDropper && (
                  <button
                    className="eyedropper-btn"
                    onClick={async () => {
                      try {
                        const eyeDropper = new (window as any).EyeDropper();
                        const result = await eyeDropper.open();
                        const newColor = result.sRGBHex;
                        const selectedObjs = getSelectedObjects();
                        
                        isChangingColorRef.current = true;
                        
                        if (selectedObjs.length === 0) {
                          // 선택된 객체가 없을 때는 기본 색상만 업데이트
                          setDefaultColor(newColor);
                          requestRender();
                        } else {
                          // 모든 선택된 객체의 색상 변경
                          selectedObjs.forEach(obj => {
                            if (obj.type === 'text') {
                              // 텍스트 객체는 color 속성 변경
                              updateObject(obj.id, (o) => ({ ...o, color: newColor }));
                            } else if (obj.type !== 'image') {
                              // 일반 객체(사각형, 원)는 backgroundColor 변경 (이미지는 제외)
                              updateObject(obj.id, (o) => ({ ...o, backgroundColor: newColor }));
                            }
                          });
                          requestRender();
                        }
                        
                        // 히스토리 저장
                        isChangingColorRef.current = false;
                        if (selectedObjs.length > 0) {
                          saveToHistory({
                            objects: new Map(objects),
                            zOrder: [...zOrder],
                            selectedId
                          });
                        }
                      } catch (err) {
                        // 사용자가 ESC로 취소하거나 오류 발생 시 무시
                        console.log('EyeDropper cancelled or error:', err);
                      }
                    }}
                    title="스포이드 도구 (화면에서 색상 선택)"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M20.71 5.63L18.37 3.29C18 2.9 17.35 2.9 16.96 3.29L15.12 5.12L16.88 6.88L18.71 5.04C19.1 4.65 19.1 4 18.71 3.63ZM17.96 7.29L16.12 9.12L4 21.24V24H6.76L18.88 11.88L20.71 10.04C21.1 9.65 21.1 9 20.71 8.63L17.96 7.29Z" fill="currentColor"/>
                    </svg>
                  </button>
                )}
              </div>
              <div className="color-picker-always-visible">
                <ChromePicker
                  color={currentColor}
                  onChange={(color) => {
                    const newColor = color.hex;
                    const selectedObjs = getSelectedObjects();
                    
                    // onChange가 처음 호출될 때 플래그 설정
                    if (!isChangingColorRef.current) {
                      isChangingColorRef.current = true;
                    }
                    
                    if (selectedObjs.length === 0) {
                      // 선택된 객체가 없을 때는 기본 색상만 업데이트
                      setDefaultColor(newColor);
                      requestRender();
                      return;
                    }
                    
                    // 모든 선택된 객체의 색상 변경
                    selectedObjs.forEach(obj => {
                      if (obj.type === 'text') {
                        // 텍스트 객체는 color 속성 변경
                        updateObject(obj.id, (o) => ({ ...o, color: newColor }));
                      } else if (obj.type !== 'image') {
                        // 일반 객체(사각형, 원)는 backgroundColor 변경 (이미지는 제외)
                        updateObject(obj.id, (o) => ({ ...o, backgroundColor: newColor }));
                      }
                    });
                    
                    requestRender();
                  }}
                  onChangeComplete={() => {
                    if (isChangingColorRef.current) {
                      isChangingColorRef.current = false;
                      const selectedObjs = getSelectedObjects();
                      // 선택된 객체가 있을 때만 히스토리 저장 (기본 색상 변경은 히스토리에 저장하지 않음)
                      if (selectedObjs.length > 0) {
                        saveToHistory({
                          objects: new Map(objects),
                          zOrder: [...zOrder],
                          selectedId
                        });
                      }
                    }
                  }}
                  disableAlpha={true}
                />
              </div>
            </div>
          </div>
        </div>

        {/* 사용된 색상 목록 - 컬러피커 아래 (객체 선택 시 숨김) */}
        {!selectedId && selectedIds.size === 0 && usedColors.length > 0 && (
          <div className="info-panel__section" ref={usedColorsSectionRef}>
            <h3 className="info-panel__title">사용된 색상</h3>
            <div className="info-panel__content">
              {/* 편집 중인 색상 컬러피커 (사용된 색상 스와치 클릭 시) */}
              {editingColor && (
                <div style={{ marginBottom: '16px', position: 'relative' }}>
                  <button
                    onClick={() => setEditingColor(null)}
                    style={{
                      position: 'absolute',
                      top: '-8px',
                      right: '0',
                      background: 'none',
                      border: 'none',
                      fontSize: '18px',
                      cursor: 'pointer',
                      color: '#6c757d',
                      padding: '4px 8px',
                      lineHeight: '1',
                      zIndex: 10
                    }}
                    title="닫기"
                  >
                    ×
                  </button>
                  <div className="color-picker-container">
                    {(window as any).EyeDropper && (
                      <div className="color-swatches">
                        <button
                          className="eyedropper-btn"
                          onClick={async () => {
                            try {
                              const eyeDropper = new (window as any).EyeDropper();
                              const result = await eyeDropper.open();
                              const newColor = result.sRGBHex.toUpperCase();
                              
                              if (!editingColor) return;
                              
                              const oldColor = editingColor.color;
                              
                              // ID 매핑 업데이트: 새 색상에 기존 ID를 재사용
                              const newColorKey = `${editingColor.type}_${newColor}`;
                              const idMap = colorIdMapRef.current;
                              const existingId = idMap.get(newColorKey);
                              
                              // 새 색상에 기존 ID가 없으면, 편집 중인 ID를 새 색상에 매핑
                              if (!existingId) {
                                // 기존 색상의 ID 매핑 제거
                                const oldColorKey = `${editingColor.type}_${oldColor}`;
                                idMap.delete(oldColorKey);
                                // 새 색상에 편집 중인 ID 매핑
                                idMap.set(newColorKey, editingColor.id);
                              }
                              
                              // editingColor의 color만 업데이트 (ID와 originalColor는 유지)
                              setEditingColor(prev => prev ? { ...prev, color: newColor } : null);
                              // 실제 객체 색상 변경
                              updateObjectsByColorType(editingColor.type, oldColor, newColor, true);
                            } catch (err) {
                              // 사용자가 ESC로 취소하거나 오류 발생 시 무시
                              console.log('EyeDropper cancelled or error:', err);
                            }
                          }}
                          title="스포이드 도구 (화면에서 색상 선택)"
                        >
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M20.71 5.63L18.37 3.29C18 2.9 17.35 2.9 16.96 3.29L15.12 5.12L16.88 6.88L18.71 5.04C19.1 4.65 19.1 4 18.71 3.63ZM17.96 7.29L16.12 9.12L4 21.24V24H6.76L18.88 11.88L20.71 10.04C21.1 9.65 21.1 9 20.71 8.63L17.96 7.29Z" fill="currentColor"/>
                          </svg>
                        </button>
                      </div>
                    )}
                    <div className="color-picker-always-visible">
                      <ChromePicker
                        color={editingColor.color}
                        onChange={(color) => {
                          if (!editingColor) return;
                          
                          // onChange가 처음 호출될 때 플래그 설정
                          if (!isChangingColorRef.current) {
                            isChangingColorRef.current = true;
                          }
                          
                          const newColor = color.hex.toUpperCase();
                          const oldColor = editingColor.color; // 현재 색상을 이전 값으로 사용
                          
                          // ID 매핑 업데이트: 새 색상에 기존 ID를 재사용
                          const newColorKey = `${editingColor.type}_${newColor}`;
                          const idMap = colorIdMapRef.current;
                          const existingId = idMap.get(newColorKey);
                          
                          // 새 색상에 기존 ID가 없으면, 편집 중인 ID를 새 색상에 매핑
                          if (!existingId) {
                            // 기존 색상의 ID 매핑 제거
                            const oldColorKey = `${editingColor.type}_${oldColor}`;
                            idMap.delete(oldColorKey);
                            // 새 색상에 편집 중인 ID 매핑
                            idMap.set(newColorKey, editingColor.id);
                          }
                          
                          // editingColor의 color만 업데이트 (ID와 originalColor는 유지)
                          setEditingColor(prev => prev ? { ...prev, color: newColor } : null);
                          // 실제 객체 색상 변경 (현재 색상을 이전 값으로 사용하여 실시간 반영, 히스토리는 저장하지 않음)
                          updateObjectsByColorType(editingColor.type, oldColor, newColor, false);
                        }}
                        onChangeComplete={() => {
                          // 드래그 완료 시에만 히스토리 저장
                          if (isChangingColorRef.current) {
                            isChangingColorRef.current = false;
                            if (editingColor) {
                              saveToHistory({ objects, zOrder, selectedId });
                            }
                          }
                        }}
                        disableAlpha={true}
                      />
                    </div>
                  </div>
                </div>
              )}
              
              {(() => {
                // 타입별로 그룹화
                const groupedColors = {
                  background: usedColors.filter(c => c.type === 'background'),
                  text: usedColors.filter(c => c.type === 'text'),
                  border: usedColors.filter(c => c.type === 'border'),
                  outglow: usedColors.filter(c => c.type === 'outglow')
                };

                const typeLabels = {
                  background: '배경',
                  text: '텍스트',
                  border: '테두리',
                  outglow: '아웃글로우'
                };

                return (
                  <div className="used-colors-grouped">
                    {(['background', 'text', 'border', 'outglow'] as const).map(type => {
                      const colors = groupedColors[type];
                      if (colors.length === 0) return null;

                      return (
                        <div key={type} className="used-colors-type-group">
                          <div className="used-colors-type-label">
                            {typeLabels[type]}
                          </div>
                          <div className="used-colors-grid">
                            {colors.map((item) => {
                              const isSelected = editingColor?.id === item.id;
                              return (
                                <div
                                  key={item.id}
                                  className="used-color-swatch"
                                  onClick={() => setEditingColor({ id: item.id, type: item.type, color: item.color, originalColor: item.color })}
                                  style={{
                                    backgroundColor: item.color,
                                    position: 'relative',
                                    borderColor: isSelected ? '#007bff' : '#dee2e6',
                                    borderWidth: isSelected ? '3px' : '2px'
                                  }}
                                  title={`${typeLabels[type]} 색상 (${item.count}개 사용)`}
                                >
                                  {/* 선택 표시 체크 아이콘 */}
                                  {isSelected && (
                                    <div style={{
                                      position: 'absolute',
                                      top: '50%',
                                      left: '50%',
                                      transform: 'translate(-50%, -50%)',
                                      fontSize: '16px',
                                      color: '#fff',
                                      textShadow: '0 0 3px rgba(0,0,0,0.8), 0 0 3px rgba(0,0,0,0.8)',
                                      fontWeight: 'bold',
                                      pointerEvents: 'none'
                                    }}>
                                      ✓
                                    </div>
                                  )}
                                  {/* 카운트 표시 */}
                                  {item.count > 1 && !isSelected && (
                                    <div style={{
                                      position: 'absolute',
                                      bottom: '2px',
                                      left: '2px',
                                      fontSize: '9px',
                                      color: '#fff',
                                      textShadow: '0 0 2px rgba(0,0,0,0.8)',
                                      fontWeight: 'bold'
                                    }}>
                                      {item.count}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          </div>
        )}

        {/* 텍스트 스타일 */}
        {selectedObject && selectedObject.type === 'text' && (
        <div className="info-panel__section">
          <h3 className="info-panel__title">텍스트 스타일</h3>
          <div className="info-panel__content">
              <div className="info-field">
                <label className="info-panel__label">폰트 크기</label>
                <input 
                  type="number" 
                  className="info-panel__input" 
                  value={tempFontSize !== '' ? tempFontSize : String(selectedObject.fontSize || 50)}
                  onFocus={(e) => {
                    focusedInputRef.current = 'fontSize';
                    e.target.select();
                  }}
                  onChange={(e) => {
                    setTempFontSize(e.target.value);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.currentTarget.blur();
                    }
                  }}
                  onBlur={(e) => {
                    focusedInputRef.current = null;
                    const newSize = Math.max(1, parseInt(e.target.value) || 50);
                    setTempFontSize('');
                    
                    const selectedObjs = getSelectedObjects();
                    const textObjects = selectedObjs.filter(obj => obj.type === 'text');
                    
                    if (textObjects.length > 0) {
                      const measureCanvas = document.createElement('canvas');
                      const measureCtx = measureCanvas.getContext('2d');
                      
                      textObjects.forEach(obj => {
                        if (measureCtx && obj.text) {
                          const fontFamily = obj.fontFamily || 'PretendardJP';
                          const fontStyle = obj.fontStyle || 'normal';
                          const fontWeight = obj.fontWeight || 'normal';
                          // 폰트 패밀리 이름을 항상 따옴표로 감싸기
                          const quotedFontFamily = `"${fontFamily}"`;
                          measureCtx.font = `${fontStyle} ${fontWeight} ${newSize}px ${quotedFontFamily}`;
                          
                          const lines = obj.text.split('\n');
                          let maxWidth = 0;
                          lines.forEach(line => {
                            const width = measureCtx.measureText(line || ' ').width;
                            if (width > maxWidth) maxWidth = width;
                          });
                          
                          const textWidth = Math.round(Math.max(maxWidth, 100));
                          const textHeight = Math.round(newSize * 1.2 * (lines.length || 1));
                          
                          const align = obj.textAlign || 'left';
                          let newX = obj.x;
                          if (align === 'center') newX = obj.x + (obj.width - textWidth) / 2;
                          else if (align === 'right') newX = obj.x + (obj.width - textWidth);
                          
                          updateObject(obj.id, (o) => ({
                            ...o,
                            fontSize: newSize,
                            width: textWidth,
                            height: textHeight,
                            x: Math.round(newX)
                          }));
                        } else {
                          updateObject(obj.id, (o) => ({
                            ...o,
                            fontSize: newSize
                          }));
                        }
                      });
                      requestRender();
                      saveToHistory({ objects, zOrder, selectedId });
                    }
                  }}
                />
          </div>
              <div className="info-field">
                <label className="info-panel__label">폰트 종류</label>
                <select 
                  className="info-panel__input" 
                  value={selectedObject.fontFamily || 'PretendardJP'}
                  disabled={fontsLoading}
                  onKeyDown={(e) => {
                    // ESC 키를 누르면 포커스 해제
                    if (e.key === 'Escape' || e.key === 'Esc') {
                      e.currentTarget.blur();
                    }
                  }}
                  onChange={(e) => {
                    const newFamily = e.target.value;
                    const selectedObjs = getSelectedObjects();
                    const textObjects = selectedObjs.filter(obj => obj.type === 'text');
                    
                    if (textObjects.length > 0) {
                      const availableWeights = getAvailableWeights(newFamily);
                      const measureCanvas = document.createElement('canvas');
                      const measureCtx = measureCanvas.getContext('2d');
                      
                      textObjects.forEach(obj => {
                        // 새 폰트 패밀리의 사용 가능한 두께 확인
                        const currentWeight = obj.fontWeight || '400';
                        // 현재 두께가 새 폰트 패밀리에 없으면 기본 두께(Regular 또는 첫 번째)로 설정
                        const newWeight = availableWeights.find(w => w.value === currentWeight) 
                          ? currentWeight 
                          : (availableWeights.find(w => w.value === '400')?.value || availableWeights[0]?.value || '400');
                        
                        // 폰트 변경 시 텍스트 크기 재계산
                        if (measureCtx && obj.text) {
                          const fontSize = obj.fontSize || 50;
                          const fontStyle = obj.fontStyle || 'normal';
                          // 폰트 패밀리 이름을 항상 따옴표로 감싸기
                          const quotedFontFamily = `"${newFamily}"`;
                          measureCtx.font = `${fontStyle} ${newWeight} ${fontSize}px ${quotedFontFamily}`;
                          
                          const lines = obj.text.split('\n');
                          let maxWidth = 0;
                          lines.forEach(line => {
                            const width = measureCtx.measureText(line || ' ').width;
                            if (width > maxWidth) maxWidth = width;
                          });
                          
                          const textWidth = Math.round(Math.max(maxWidth, 100));
                          const textHeight = Math.round(fontSize * 1.2 * (lines.length || 1));
                          
                          const align = obj.textAlign || 'left';
                          let newX = obj.x;
                          if (align === 'center') newX = obj.x + (obj.width - textWidth) / 2;
                          else if (align === 'right') newX = obj.x + (obj.width - textWidth);
                          
                          updateObject(obj.id, (o) => ({
                            ...o,
                            fontFamily: newFamily,
                            fontWeight: newWeight,
                            width: textWidth,
                            height: textHeight,
                            x: Math.round(newX)
                          }));
                        } else {
                          updateObject(obj.id, (o) => ({
                            ...o,
                            fontFamily: newFamily,
                            fontWeight: newWeight
                          }));
                        }
                      });
                      requestRender();
                    }
                  }}
                  onBlur={() => {
                    saveToHistory({ objects, zOrder, selectedId });
                  }}
                >
                  {groupedFontsByLanguage.map((group) => (
                    <optgroup key={group.language} label={`${getLanguageIcon(group.language)} ${group.language}`}>
                      {group.families.map((family) => (
                        <option key={family.family} value={family.family}>
                          {family.name}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
        </div>
              <div className="info-field">
                <label className="info-panel__label">폰트 두께</label>
                <select 
                  className="info-panel__input" 
                  value={selectedObject.fontWeight || '400'}
                  disabled={fontsLoading}
                  onChange={(e) => {
                    const newWeight = e.target.value;
                    const selectedObjs = getSelectedObjects();
                    const textObjects = selectedObjs.filter(obj => obj.type === 'text');
                    
                    if (textObjects.length > 0) {
                      const measureCanvas = document.createElement('canvas');
                      const measureCtx = measureCanvas.getContext('2d');
                      
                      textObjects.forEach(obj => {
                        // 폰트 두께 변경 시 텍스트 크기 재계산 (폰트에 따라 크기가 달라질 수 있음)
                        if (measureCtx && obj.text) {
                          const fontSize = obj.fontSize || 50;
                          const fontFamily = obj.fontFamily || 'PretendardJP';
                          const fontStyle = obj.fontStyle || 'normal';
                          // 폰트 패밀리 이름을 항상 따옴표로 감싸기
                          const quotedFontFamily = `"${fontFamily}"`;
                          measureCtx.font = `${fontStyle} ${newWeight} ${fontSize}px ${quotedFontFamily}`;
                          
                          const lines = obj.text.split('\n');
                          let maxWidth = 0;
                          lines.forEach(line => {
                            const width = measureCtx.measureText(line || ' ').width;
                            if (width > maxWidth) maxWidth = width;
                          });
                          
                          const textWidth = Math.round(Math.max(maxWidth, 100));
                          const textHeight = Math.round(fontSize * 1.2 * (lines.length || 1));
                          
                          const align = obj.textAlign || 'left';
                          let newX = obj.x;
                          if (align === 'center') newX = obj.x + (obj.width - textWidth) / 2;
                          else if (align === 'right') newX = obj.x + (obj.width - textWidth);
                          
                          updateObject(obj.id, (o) => ({
                            ...o,
                            fontWeight: newWeight,
                            width: textWidth,
                            height: textHeight,
                            x: Math.round(newX)
                          }));
                        } else {
                          updateObject(obj.id, (o) => ({
                            ...o,
                            fontWeight: newWeight
                          }));
                        }
                      });
                      requestRender();
                    }
                  }}
                  onBlur={() => {
                    saveToHistory({ objects, zOrder, selectedId });
                  }}
                >
                  {getAvailableWeights(selectedObject.fontFamily).map((weight) => (
                    <option key={weight.value} value={weight.value}>
                      {weight.name}
                    </option>
                  ))}
                  {getAvailableWeights(selectedObject.fontFamily).length === 0 && (
                    <>
                      <option value="normal">normal</option>
                      <option value="bold">bold</option>
                    </>
                  )}
                </select>
              </div>
              <div className="info-field">
                <label className="info-panel__label">정렬</label>
                <div style={{ display:'flex', gap:8 }}>
                  <button 
                    className="info-panel__button" 
                    style={{ 
                      backgroundColor: (selectedObject.textAlign || 'left') === 'left' ? '#e9ecef' : '#ffffff',
                      border: '1px solid #e0e0e0',
                      color: '#333',
                      padding: '6px 10px',
                      borderRadius: '6px'
                    }}
                    onClick={() => {
                      const selectedObjs = getSelectedObjects();
                      const textObjects = selectedObjs.filter(obj => obj.type === 'text' && obj.text);
                      
                      if (textObjects.length > 0) {
                        // 정렬 변경 시 텍스트 크기 재계산 및 위치 조정
                        const measureCanvas = document.createElement('canvas');
                        const measureCtx = measureCanvas.getContext('2d');
                        
                        if (measureCtx) {
                          textObjects.forEach(obj => {
                            if (!obj.text) return;
                            
                            const fontSize = obj.fontSize || 50;
                            const fontFamily = obj.fontFamily || 'PretendardJP';
                            const fontStyle = obj.fontStyle || 'normal';
                            const fontWeight = obj.fontWeight || 'normal';
                            // 폰트 패밀리 이름을 항상 따옴표로 감싸기
          const quotedFontFamily = `"${fontFamily}"`;
          measureCtx.font = `${fontStyle} ${fontWeight} ${fontSize}px ${quotedFontFamily}`;
                            
                            const lines = obj.text.split('\n');
                            let maxWidth = 0;
                            lines.forEach(line => {
                              const width = measureCtx.measureText(line || ' ').width;
                              if (width > maxWidth) maxWidth = width;
                            });
                            
                            const textWidth = Math.round(Math.max(maxWidth, 100));
                            const newX = obj.x; // left 정렬은 x 위치 유지
                            
                            updateObject(obj.id, (o) => ({
                              ...o,
                              textAlign: 'left',
                              width: textWidth,
                              x: newX
                            }));
                          });
                          requestRender();
                          saveToHistory({ objects, zOrder, selectedId });
                        }
                      }
                    }}
                  >좌</button>
                  <button 
                    className="info-panel__button" 
                    style={{ 
                      backgroundColor: (selectedObject.textAlign || 'left') === 'center' ? '#e9ecef' : '#ffffff',
                      border: '1px solid #e0e0e0',
                      color: '#333',
                      padding: '6px 10px',
                      borderRadius: '6px'
                    }}
                    onClick={() => {
                      const selectedObjs = getSelectedObjects();
                      const textObjects = selectedObjs.filter(obj => obj.type === 'text' && obj.text);
                      
                      if (textObjects.length > 0) {
                        const measureCanvas = document.createElement('canvas');
                        const measureCtx = measureCanvas.getContext('2d');
                        
                        if (measureCtx) {
                          textObjects.forEach(obj => {
                            if (!obj.text) return;
                            
                            const fontSize = obj.fontSize || 50;
                            const fontFamily = obj.fontFamily || 'PretendardJP';
                            const fontStyle = obj.fontStyle || 'normal';
                            const fontWeight = obj.fontWeight || 'normal';
                            // 폰트 패밀리 이름을 항상 따옴표로 감싸기
          const quotedFontFamily = `"${fontFamily}"`;
          measureCtx.font = `${fontStyle} ${fontWeight} ${fontSize}px ${quotedFontFamily}`;
                            
                            const lines = obj.text.split('\n');
                            let maxWidth = 0;
                            lines.forEach(line => {
                              const width = measureCtx.measureText(line || ' ').width;
                              if (width > maxWidth) maxWidth = width;
                            });
                            
                            const textWidth = Math.round(Math.max(maxWidth, 100));
                            const newX = obj.x + (obj.width - textWidth) / 2;
                            
                            updateObject(obj.id, (o) => ({
                              ...o,
                              textAlign: 'center',
                              width: textWidth,
                              x: Math.round(newX)
                            }));
                          });
                          requestRender();
                          saveToHistory({ objects, zOrder, selectedId });
                        }
                      }
                    }}
                  >중</button>
                  <button 
                    className="info-panel__button" 
                    style={{ 
                      backgroundColor: (selectedObject.textAlign || 'left') === 'right' ? '#e9ecef' : '#ffffff',
                      border: '1px solid #e0e0e0',
                      color: '#333',
                      padding: '6px 10px',
                      borderRadius: '6px'
                    }}
                    onClick={() => {
                      const selectedObjs = getSelectedObjects();
                      const textObjects = selectedObjs.filter(obj => obj.type === 'text' && obj.text);
                      
                      if (textObjects.length > 0) {
                        const measureCanvas = document.createElement('canvas');
                        const measureCtx = measureCanvas.getContext('2d');
                        
                        if (measureCtx) {
                          textObjects.forEach(obj => {
                            if (!obj.text) return;
                            
                            const fontSize = obj.fontSize || 50;
                            const fontFamily = obj.fontFamily || 'PretendardJP';
                            const fontStyle = obj.fontStyle || 'normal';
                            const fontWeight = obj.fontWeight || 'normal';
                            // 폰트 패밀리 이름을 항상 따옴표로 감싸기
          const quotedFontFamily = `"${fontFamily}"`;
          measureCtx.font = `${fontStyle} ${fontWeight} ${fontSize}px ${quotedFontFamily}`;
                            
                            const lines = obj.text.split('\n');
                            let maxWidth = 0;
                            lines.forEach(line => {
                              const width = measureCtx.measureText(line || ' ').width;
                              if (width > maxWidth) maxWidth = width;
                            });
                            
                            const textWidth = Math.round(Math.max(maxWidth, 100));
                            const newX = obj.x + (obj.width - textWidth);
                            
                            updateObject(obj.id, (o) => ({
                              ...o,
                              textAlign: 'right',
                              width: textWidth,
                              x: Math.round(newX)
                            }));
                          });
                          requestRender();
                          saveToHistory({ objects, zOrder, selectedId });
                        }
                      }
                    }}
                  >우</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 정보 */}
        {getSelectedObjects().length > 0 && (
          <div className="info-panel__section">
            <h3 className="info-panel__title">정보</h3>
            <div className="info-panel__content">
              {getSelectedObjects().length > 0 ? (
              <>
                <div className="info-panel__row--two">
                  <div className="info-field">
                    <label className="info-panel__label">X</label>
                    <input 
                      className="info-panel__input" 
                      type="number" 
                      value={inputValues.x}
                      onFocus={(e) => {
                        focusedInputRef.current = 'x';
                        e.target.select();
                      }}
                      onChange={(e) => setInputValues(prev => ({ ...prev, x: e.target.value }))}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.currentTarget.blur();
                        }
                      }}
                      onBlur={(e) => {
                        const val = parseFloat(e.target.value);
                        focusedInputRef.current = null;
                        if (!isNaN(val)) {
                          const selectedObjs = getSelectedObjects();
                          if (selectedObjs.length > 0) {
                            if (selectedObjs.length === 1) {
                              // 단일 선택: AABB 기준으로 이동
                              const obj = selectedObjs[0];
                              
                              // 현재 AABB 계산
                              const currentAABB = getRotatedAABB(obj);
                              
                              // 입력값을 절대 좌표로 변환
                              let targetAabbX = val;
                              if (obj.artboardId) {
                                const artboard = artboardsMap.get(obj.artboardId);
                                if (artboard) {
                                  targetAabbX = artboard.x + val;
                                }
                              }
                              
                              // AABB의 현재 좌표와 목표 좌표의 차이 계산
                              const dx = targetAabbX - currentAABB.minX;
                              
                              // 객체를 dx만큼 이동 (회전은 유지)
                              updateObject(obj.id, (o) => ({ ...o, x: Math.round(o.x + dx) }));
                            } else {
                              // 다중 선택: AABB 기준으로 그룹 전체 이동
                              // 모든 객체가 같은 아트보드에 속하는지 확인
                              const firstArtboardId = selectedObjs[0].artboardId;
                              const allSameArtboard = selectedObjs.every(obj => obj.artboardId === firstArtboardId);
                              const artboard = firstArtboardId ? artboardsMap.get(firstArtboardId) : null;
                              
                              const aabb = computeRotatedAABB(selectedObjs);
                              if (aabb) {
                                // AABB의 현재 표시 좌표 계산 (상대 좌표 기준)
                                let currentDisplayX = aabb.x;
                                if (allSameArtboard && artboard) {
                                  currentDisplayX = aabb.x - artboard.x;
                                }
                                
                                const dx = val - currentDisplayX;
                                selectedObjs.forEach(obj => {
                                  updateObject(obj.id, (o) => ({ ...o, x: Math.round(o.x + dx) }));
                                });
                              }
                            }
                          requestRender();
                            saveToHistory({ objects, zOrder, selectedId });
                          }
                        }
                      }}
                    />
                  </div>
                  <div className="info-field">
                    <label className="info-panel__label">Y</label>
                    <input 
                      className="info-panel__input" 
                      type="number" 
                      value={inputValues.y}
                      onFocus={(e) => {
                        focusedInputRef.current = 'y';
                        e.target.select();
                      }}
                      onChange={(e) => setInputValues(prev => ({ ...prev, y: e.target.value }))}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.currentTarget.blur();
                        }
                      }}
                      onBlur={(e) => {
                        const val = parseFloat(e.target.value);
                        focusedInputRef.current = null;
                        if (!isNaN(val)) {
                          const selectedObjs = getSelectedObjects();
                          if (selectedObjs.length > 0) {
                            if (selectedObjs.length === 1) {
                              // 단일 선택: AABB 기준으로 이동
                              const obj = selectedObjs[0];
                              
                              // 현재 AABB 계산
                              const currentAABB = getRotatedAABB(obj);
                              
                              // 입력값을 절대 좌표로 변환
                              let targetAabbY = val;
                              if (obj.artboardId) {
                                const artboard = artboardsMap.get(obj.artboardId);
                                if (artboard) {
                                  targetAabbY = artboard.y + val;
                                }
                              }
                              
                              // AABB의 현재 좌표와 목표 좌표의 차이 계산
                              const dy = targetAabbY - currentAABB.minY;
                              
                              // 객체를 dy만큼 이동 (회전은 유지)
                              updateObject(obj.id, (o) => ({ ...o, y: Math.round(o.y + dy) }));
                            } else {
                              // 다중 선택: AABB 기준으로 그룹 전체 이동
                              // 모든 객체가 같은 아트보드에 속하는지 확인
                              const firstArtboardId = selectedObjs[0].artboardId;
                              const allSameArtboard = selectedObjs.every(obj => obj.artboardId === firstArtboardId);
                              const artboard = firstArtboardId ? artboardsMap.get(firstArtboardId) : null;
                              
                              const aabb = computeRotatedAABB(selectedObjs);
                              if (aabb) {
                                // AABB의 현재 표시 좌표 계산 (상대 좌표 기준)
                                let currentDisplayY = aabb.y;
                                if (allSameArtboard && artboard) {
                                  currentDisplayY = aabb.y - artboard.y;
                                }
                                
                                const dy = val - currentDisplayY;
                                selectedObjs.forEach(obj => {
                                  updateObject(obj.id, (o) => ({ ...o, y: Math.round(o.y + dy) }));
                                });
                              }
                            }
                          requestRender();
                            saveToHistory({ objects, zOrder, selectedId });
                          }
                        }
                      }}
                    />
                  </div>
                </div>
                <div className="info-panel__row--two">
                  <div className="info-field">
                    <label className="info-panel__label">너비</label>
                    <input 
                      className="info-panel__input" 
                      type="number" 
                      min="1"
                      value={inputValues.width}
                      onFocus={(e) => {
                        focusedInputRef.current = 'width';
                        e.target.select();
                      }}
                      onChange={(e) => setInputValues(prev => ({ ...prev, width: e.target.value }))}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.currentTarget.blur();
                        }
                      }}
                      onBlur={(e) => {
                        const val = parseFloat(e.target.value);
                        focusedInputRef.current = null;
                        if (!isNaN(val) && val >= 1) {
                          const selectedObjs = getSelectedObjects();
                          if (selectedObjs.length > 0) {
                            selectedObjs.forEach(obj => {
                              updateObject(obj.id, (o) => {
                            // 중심점 기준으로 크기 변경
                                const cx = o.x + o.width / 2;
                                const cy = o.y + o.height / 2;
                            const newWidth = Math.round(val);
                            const newX = Math.round(cx - newWidth / 2);
                                return { ...o, x: newX, width: newWidth };
                              });
                          });
                          requestRender();
                            saveToHistory({ objects, zOrder, selectedId });
                          }
                        }
                      }}
                    />
                  </div>
                  <div className="info-field">
                    <label className="info-panel__label">높이</label>
                    <input 
                      className="info-panel__input" 
                      type="number" 
                      min="1"
                      value={inputValues.height}
                      onFocus={(e) => {
                        focusedInputRef.current = 'height';
                        e.target.select();
                      }}
                      onChange={(e) => setInputValues(prev => ({ ...prev, height: e.target.value }))}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.currentTarget.blur();
                        }
                      }}
                      onBlur={(e) => {
                        const val = parseFloat(e.target.value);
                        focusedInputRef.current = null;
                        if (!isNaN(val) && val >= 1) {
                          const selectedObjs = getSelectedObjects();
                          if (selectedObjs.length > 0) {
                            selectedObjs.forEach(obj => {
                              updateObject(obj.id, (o) => {
                            // 중심점 기준으로 크기 변경
                                const cx = o.x + o.width / 2;
                                const cy = o.y + o.height / 2;
                            const newHeight = Math.round(val);
                            const newY = Math.round(cy - newHeight / 2);
                                return { ...o, y: newY, height: newHeight };
                              });
                          });
                          requestRender();
                            saveToHistory({ objects, zOrder, selectedId });
                          }
                        }
                      }}
                    />
                  </div>
                </div>
                <div className="info-field">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    <input
                      type="checkbox"
                      id="rotate-as-group"
                      checked={rotateAsGroup}
                      onChange={(e) => setRotateAsGroup(e.target.checked)}
                      disabled={getSelectedObjects().length <= 1}
                    />
                    <label 
                      htmlFor="rotate-as-group" 
                      className="info-panel__label"
                      style={{ margin: 0, cursor: getSelectedObjects().length <= 1 ? 'not-allowed' : 'pointer', opacity: getSelectedObjects().length <= 1 ? 0.5 : 1 }}
                    >
                      그룹으로 회전
                    </label>
                  </div>
                  <input 
                    className="info-panel__input" 
                    type="number" 
                    value={inputValues.rotation}
                    onFocus={(e) => {
                      focusedInputRef.current = 'rotation';
                      e.target.select();
                    }}
                    onChange={(e) => setInputValues(prev => ({ ...prev, rotation: e.target.value }))}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.currentTarget.blur();
                      }
                    }}
                    onBlur={(e) => {
                      const val = parseFloat(e.target.value);
                      focusedInputRef.current = null;
                      if (!isNaN(val)) {
                        const radians = val * (Math.PI / 180); // 도를 라디안으로 변환
                        const selectedObjs = getSelectedObjects();
                        if (selectedObjs.length > 0) {
                          if (rotateAsGroup && selectedObjs.length > 1) {
                            // 그룹 회전: AABB 중심 기준으로 그룹 전체 회전
                            const aabb = computeAABB(selectedObjs);
                            if (aabb) {
                              const centerX = aabb.x + aabb.width / 2;
                              const centerY = aabb.y + aabb.height / 2;
                              
                              selectedObjs.forEach(obj => {
                                // 객체의 현재 중심점
                                const objCenterX = obj.x + obj.width / 2;
                                const objCenterY = obj.y + obj.height / 2;
                                
                                // AABB 중심 기준 상대 위치
                                const relX = objCenterX - centerX;
                                const relY = objCenterY - centerY;
                                
                                // 현재 회전 각도와 목표 회전 각도의 차이
                                const currentRotation = obj.rotation || 0;
                                const deltaAngle = radians - currentRotation;
                                
                                // 회전 변환 (cos, sin)
                                const cos = Math.cos(deltaAngle);
                                const sin = Math.sin(deltaAngle);
                                
                                // 회전된 상대 위치
                                const newRelX = relX * cos - relY * sin;
                                const newRelY = relX * sin + relY * cos;
                                
                                // 새로운 객체 중심 위치
                                const newObjCenterX = centerX + newRelX;
                                const newObjCenterY = centerY + newRelY;
                                
                                // 새로운 객체 위치 (중심 기준)
                                const newX = Math.round(newObjCenterX - obj.width / 2);
                                const newY = Math.round(newObjCenterY - obj.height / 2);
                                
                                updateObject(obj.id, (o) => ({
                                  ...o,
                                  x: newX,
                                  y: newY,
                                  rotation: radians
                                }));
                              });
                            }
                          } else {
                            // 개별 회전: 각 객체의 rotation만 변경
                            selectedObjs.forEach(obj => {
                              updateObject(obj.id, (o) => ({ ...o, rotation: radians }));
                            });
                          }
                        requestRender();
                          saveToHistory({ objects, zOrder, selectedId });
                        }
                      }
                    }}
                  />
                </div>
              </>
            ) : (
              <>
                <div className="info-panel__row--two">
                  <div className="info-field"><label className="info-panel__label">X</label><input className="info-panel__input" type="number" disabled value="" /></div>
                  <div className="info-field"><label className="info-panel__label">Y</label><input className="info-panel__input" type="number" disabled value="" /></div>
                </div>
                <div className="info-panel__row--two">
                  <div className="info-field"><label className="info-panel__label">너비</label><input className="info-panel__input" type="number" disabled value="" /></div>
                  <div className="info-field"><label className="info-panel__label">높이</label><input className="info-panel__input" type="number" disabled value="" /></div>
                </div>
                <div className="info-field"><label className="info-panel__label">회전(도)</label><input className="info-panel__input" type="number" disabled value="" /></div>
              </>
            )}
            {/* 아트보드 선택 및 고정 */}
            {getSelectedObjects().length >= 1 && (
              <>
                <div className="info-panel__row artboard-lock-row">
                  <label className="info-panel__label">
                    {(() => {
                      const selectedObjs = getSelectedObjects();
                      // 모든 선택된 객체가 고정되어 있는지 확인
                      const allLocked = selectedObjs.length > 0 && selectedObjs.every(obj => obj.artboardLocked);
                      const noneLocked = selectedObjs.length > 0 && selectedObjs.every(obj => !obj.artboardLocked);
                      const isIndeterminate = !allLocked && !noneLocked;
                      
                      return (
                        <>
                          <input
                            ref={(checkbox) => {
                              if (checkbox) {
                                checkbox.indeterminate = isIndeterminate;
                              }
                            }}
                            type="checkbox"
                            checked={allLocked}
                            onChange={(e) => {
                              const selectedObjs = getSelectedObjects();
                              selectedObjs.forEach(obj => {
                                updateObject(obj.id, (o) => ({ ...o, artboardLocked: e.target.checked }));
                              });
                              
                              // 체크 해제 시 아트보드 자동 검사
                              if (!e.target.checked) {
                                // 상태 업데이트가 완료된 후 실행하기 위해 setTimeout 사용
                                setTimeout(() => {
                                  updateArtboardAssignments();
                                }, 0);
                              }
                              
                              requestRender();
                            }}
                          />
                          <span>아트보드 고정</span>
                        </>
                      );
                    })()}
                  </label>
                  
                  {(() => {
                    const selectedObjs = getSelectedObjects();
                    // 모든 선택된 객체가 고정되어 있는지 확인
                    const allLocked = selectedObjs.length > 0 && selectedObjs.every(obj => obj.artboardLocked);
                    // 모든 선택된 객체가 같은 아트보드를 가지고 있는지 확인
                    const sameArtboard = selectedObjs.length > 0 && selectedObjs.every(obj => obj.artboardId === selectedObjs[0].artboardId);
                    
                    if (allLocked && sameArtboard) {
                      const currentArtboardId = selectedObjs[0].artboardId;
                      return (
                        <div className="artboard-lock__controls">
                          <select
                            className="info-panel__select"
                            value={currentArtboardId || ''}
                            onChange={(e) => {
                              const selectedObjs = getSelectedObjects();
                              selectedObjs.forEach(obj => {
                                updateObject(obj.id, (o) => ({ ...o, artboardId: e.target.value || undefined }));
                              });
                              requestRender();
                            }}
                          >
                            <option value="">없음 (아트보드 외부)</option>
                            {initialArtboards.map(artboard => (
                              <option key={artboard.id} value={artboard.id}>
                                {artboard.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      );
                    } else if (allLocked && !sameArtboard) {
                      // 모두 고정되어 있지만 다른 아트보드에 고정된 경우
                      return (
                        <div className="artboard-lock__controls">
                          <select
                            className="info-panel__select info-panel__select--mixed"
                            value="---"
                            onChange={(e) => {
                              const selectedObjs = getSelectedObjects();
                              selectedObjs.forEach(obj => {
                                updateObject(obj.id, (o) => ({ ...o, artboardId: e.target.value === "---" ? undefined : e.target.value || undefined }));
                              });
                              requestRender();
                            }}
                          >
                            <option value="---">--- (여러 아트보드)</option>
                            <option value="">없음 (아트보드 외부)</option>
                            {initialArtboards.map(artboard => (
                              <option key={artboard.id} value={artboard.id}>
                                {artboard.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
                
                {/* 아트보드 정보 표시 (고정되지 않은 경우) */}
                {(() => {
                  const selectedObjs = getSelectedObjects();
                  const allLocked = selectedObjs.length > 0 && selectedObjs.every(obj => obj.artboardLocked);
                  
                  if (!allLocked) {
                    const sameArtboard = selectedObjs.length > 0 && selectedObjs.every(obj => obj.artboardId === selectedObjs[0].artboardId);
                    const currentArtboardId = selectedObjs[0]?.artboardId;
                    const artboard = currentArtboardId ? initialArtboards.find(ab => ab.id === currentArtboardId) : null;
                    
                    return (
            <div className="info-panel__row artboard-row">
              <label className="info-panel__label">속한 아트보드</label>
                        <span className="artboard-info__value">
                          {sameArtboard && artboard ? artboard.name : sameArtboard ? '없음' : '--- (여러 아트보드)'}
                        </span>
            </div>
                    );
                  }
                  return null;
                })()}
              </>
            )}
            {/* 둥근 모서리 */}
            {selectedId && selectedObject && selectedObject.type === 'rectangle' && (
              <div className="info-panel__row">
                <label className="info-panel__label">둥근 모서리</label>
                <div className="border-radius-control">
                  <button 
                    className={`link-btn ${borderRadiusLinked ? 'linked' : ''}`}
                    onClick={() => setBorderRadiusLinked(!borderRadiusLinked)}
                    title={borderRadiusLinked ? '연결됨: 모든 모서리가 함께 변경' : '연결 해제: 각 모서리 독립 설정'}
                  >
                    {borderRadiusLinked ? '🔗' : '═'}
                  </button>
                  <div className="border-radius-corners">
                    <div className="border-radius-corner">
                      <label>좌상</label>
                      <input 
                        type="number" 
                        className="info-panel__input" 
                        min="0"
                        value={inputValues.borderRadiusTopLeft}
                        onFocus={(e) => {
                          focusedInputRef.current = 'borderRadiusTopLeft';
                          e.target.select();
                        }}
                        onChange={(e) => {
                          const value = e.target.value;
                          setInputValues(prev => {
                            if (borderRadiusLinked) {
                              return {
                                ...prev,
                                borderRadiusTopLeft: value,
                                borderRadiusTopRight: value,
                                borderRadiusBottomLeft: value,
                                borderRadiusBottomRight: value
                              };
                            }
                            return { ...prev, borderRadiusTopLeft: value };
                          });
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.currentTarget.blur();
                          }
                        }}
                        onBlur={(e) => {
                          focusedInputRef.current = null;
                          const selectedObjs = getSelectedObjects();
                          if (selectedObjs.length === 0) return;
                          const val = Math.max(0, parseFloat(e.target.value) || 0);
                          // 선택된 객체 중 사각형 객체에만 적용
                          selectedObjs.forEach(obj => {
                            if (obj.type === 'rectangle') {
                              updateObject(obj.id, (o) => {
                            if (borderRadiusLinked) {
                              return {
                                    ...o,
                                borderRadiusTopLeft: val,
                                borderRadiusTopRight: val,
                                borderRadiusBottomLeft: val,
                                borderRadiusBottomRight: val
                              };
                            }
                                return { ...o, borderRadiusTopLeft: val };
                              });
                            }
                          });
                          requestRender();
                          saveToHistory({ objects: new Map(objects), zOrder: [...zOrder], selectedId });
                        }}
                      />
                    </div>
                    <div className="border-radius-corner">
                      <label>우상</label>
                      <input 
                        type="number" 
                        className="info-panel__input" 
                        min="0"
                        value={inputValues.borderRadiusTopRight}
                        onFocus={(e) => {
                          focusedInputRef.current = 'borderRadiusTopRight';
                          e.target.select();
                        }}
                        onChange={(e) => {
                          const value = e.target.value;
                          setInputValues(prev => {
                            if (borderRadiusLinked) {
                              return {
                                ...prev,
                                borderRadiusTopLeft: value,
                                borderRadiusTopRight: value,
                                borderRadiusBottomLeft: value,
                                borderRadiusBottomRight: value
                              };
                            }
                            return { ...prev, borderRadiusTopRight: value };
                          });
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.currentTarget.blur();
                          }
                        }}
                        onBlur={(e) => {
                          focusedInputRef.current = null;
                          const selectedObjs = getSelectedObjects();
                          if (selectedObjs.length === 0) return;
                          const val = Math.max(0, parseFloat(e.target.value) || 0);
                          // 선택된 객체 중 사각형 객체에만 적용
                          selectedObjs.forEach(obj => {
                            if (obj.type === 'rectangle') {
                              updateObject(obj.id, (o) => {
                            if (borderRadiusLinked) {
                              return {
                                    ...o,
                                borderRadiusTopLeft: val,
                                borderRadiusTopRight: val,
                                borderRadiusBottomLeft: val,
                                borderRadiusBottomRight: val
                              };
                            }
                                return { ...o, borderRadiusTopRight: val };
                              });
                            }
                          });
                          requestRender();
                          saveToHistory({ objects: new Map(objects), zOrder: [...zOrder], selectedId });
                        }}
                      />
                    </div>
                    <div className="border-radius-corner">
                      <label>좌하</label>
                      <input 
                        type="number" 
                        className="info-panel__input" 
                        min="0"
                        value={inputValues.borderRadiusBottomLeft}
                        onFocus={(e) => {
                          focusedInputRef.current = 'borderRadiusBottomLeft';
                          e.target.select();
                        }}
                        onChange={(e) => {
                          const value = e.target.value;
                          setInputValues(prev => {
                            if (borderRadiusLinked) {
                              return {
                                ...prev,
                                borderRadiusTopLeft: value,
                                borderRadiusTopRight: value,
                                borderRadiusBottomLeft: value,
                                borderRadiusBottomRight: value
                              };
                            }
                            return { ...prev, borderRadiusBottomLeft: value };
                          });
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.currentTarget.blur();
                          }
                        }}
                        onBlur={(e) => {
                          focusedInputRef.current = null;
                          const selectedObjs = getSelectedObjects();
                          if (selectedObjs.length === 0) return;
                          const val = Math.max(0, parseFloat(e.target.value) || 0);
                          // 선택된 객체 중 사각형 객체에만 적용
                          selectedObjs.forEach(obj => {
                            if (obj.type === 'rectangle') {
                              updateObject(obj.id, (o) => {
                            if (borderRadiusLinked) {
                              return {
                                    ...o,
                                borderRadiusTopLeft: val,
                                borderRadiusTopRight: val,
                                borderRadiusBottomLeft: val,
                                borderRadiusBottomRight: val
                              };
                            }
                                return { ...o, borderRadiusBottomLeft: val };
                              });
                            }
                          });
                          requestRender();
                          saveToHistory({ objects: new Map(objects), zOrder: [...zOrder], selectedId });
                        }}
                      />
                    </div>
                    <div className="border-radius-corner">
                      <label>우하</label>
                      <input 
                        type="number" 
                        className="info-panel__input" 
                        min="0"
                        value={inputValues.borderRadiusBottomRight}
                        onFocus={(e) => {
                          focusedInputRef.current = 'borderRadiusBottomRight';
                          e.target.select();
                        }}
                        onChange={(e) => {
                          const value = e.target.value;
                          setInputValues(prev => {
                            if (borderRadiusLinked) {
                              return {
                                ...prev,
                                borderRadiusTopLeft: value,
                                borderRadiusTopRight: value,
                                borderRadiusBottomLeft: value,
                                borderRadiusBottomRight: value
                              };
                            }
                            return { ...prev, borderRadiusBottomRight: value };
                          });
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.currentTarget.blur();
                          }
                        }}
                        onBlur={(e) => {
                          focusedInputRef.current = null;
                          const selectedObjs = getSelectedObjects();
                          if (selectedObjs.length === 0) return;
                          const val = Math.max(0, parseFloat(e.target.value) || 0);
                          // 선택된 객체 중 사각형 객체에만 적용
                          selectedObjs.forEach(obj => {
                            if (obj.type === 'rectangle') {
                              updateObject(obj.id, (o) => {
                            if (borderRadiusLinked) {
                              return {
                                    ...o,
                                borderRadiusTopLeft: val,
                                borderRadiusTopRight: val,
                                borderRadiusBottomLeft: val,
                                borderRadiusBottomRight: val
                              };
                            }
                                return { ...o, borderRadiusBottomRight: val };
                              });
                            }
                          });
                          requestRender();
                          saveToHistory({ objects: new Map(objects), zOrder: [...zOrder], selectedId });
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        )}

        {/* 테두리 효과 */}
        {getSelectedObjects().length > 0 && selectedObject && (
          <div className="info-panel__section">
            <button 
              type="button" 
              className="info-panel__title info-panel__title--toggle" 
              onClick={() => setIsBorderEffectsOpen(prev => !prev)}
              aria-expanded={isBorderEffectsOpen} 
              aria-controls="right-panel-border-effects"
            >
              <span className="info-panel__title-text">테두리 효과</span>
              <span className="info-panel__title-arrow" aria-hidden="true"></span>
            </button>
            {isBorderEffectsOpen && (
              <div id="right-panel-border-effects" className="info-panel__content">
                {/* 테두리 두께 */}
                <div className="info-field">
                  <label className="info-panel__label">테두리 두께</label>
                  <input 
                    type="number" 
                    className="info-panel__input" 
                    min="0"
                    max="300"
                    value={tempBorderWidth !== '' ? tempBorderWidth : String(selectedBorderWidth)}
                    onFocus={(e) => {
                      focusedInputRef.current = 'borderWidth';
                      e.target.select();
                    }}
                    onChange={(e) => {
                      setTempBorderWidth(e.target.value);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.currentTarget.blur();
                      }
                    }}
                    onBlur={(e) => {
                      focusedInputRef.current = null;
                      const value = Math.max(0, Math.min(300, parseInt(e.target.value) || 0));
                      setTempBorderWidth('');
                      const selectedObjs = getSelectedObjects();
                      if (selectedObjs.length > 0) {
                        selectedObjs.forEach(obj => {
                          updateObject(obj.id, (o) => {
                        const updated: Partial<CanvasObject> = { borderWidth: value };
                        // 두께가 1 이상이면 기본값 자동 적용
                        if (value > 0) {
                              if (!o.borderColor) {
                            updated.borderColor = '#000000'; // 기본 색상
                          }
                              if (!o.borderPosition) {
                            updated.borderPosition = 'center'; // 기본 위치
                          }
                        }
                            return { ...o, ...updated };
                          });
                      });
                      requestRender();
                        saveToHistory({ objects, zOrder, selectedId });
                      }
                    }}
                  />
                  <span className="info-panel__hint">0 = 테두리 없음</span>
                </div>

                {/* 테두리 색상 */}
                <div className="info-field">
                  <label className="info-panel__label">테두리 색상</label>
                  <div className="color-picker-container">
                    <div className="color-swatches">
                      <div 
                        className="color-swatch current-color"
                        style={{ 
                          backgroundColor: selectedObject.borderColor || '#000000'
                        }}
                        title="현재 테두리 색상"
                      />
                    </div>
                    <input
                      type="color"
                      value={selectedObject.borderColor || '#000000'}
                      onMouseDown={() => {
                        isChangingColorRef.current = true;
                      }}
                      onChange={(e) => {
                        // onChange가 처음 호출될 때 플래그 설정 (onMouseDown보다 먼저 실행될 수 있음)
                        if (!isChangingColorRef.current) {
                          isChangingColorRef.current = true;
                        }
                        const newColor = e.target.value;
                        const selectedObjs = getSelectedObjects();
                        if (selectedObjs.length > 0) {
                          selectedObjs.forEach(obj => {
                            updateObject(obj.id, (o) => ({ ...o, borderColor: newColor }));
                          });
                        requestRender();
                        }
                      }}
                      onMouseUp={() => {
                        if (isChangingColorRef.current) {
                          isChangingColorRef.current = false;
                          const selectedObjs = getSelectedObjects();
                          // 컬러피커 드래그 완료 시 히스토리 저장
                          if (selectedObjs.length > 0) {
                            saveToHistory({
                              objects: new Map(objects),
                              zOrder: [...zOrder],
                              selectedId
                            });
                          }
                        }
                      }}
                      onMouseLeave={() => {
                        if (isChangingColorRef.current) {
                          isChangingColorRef.current = false;
                          const selectedObjs = getSelectedObjects();
                          // 컬러피커 드래그 완료 시 히스토리 저장
                          if (selectedObjs.length > 0) {
                            saveToHistory({
                              objects: new Map(objects),
                              zOrder: [...zOrder],
                              selectedId
                            });
                          }
                        }
                      }}
                      style={{
                        width: '100%',
                        height: '40px',
                        border: '1px solid #dee2e6',
                        borderRadius: '8px',
                        cursor: 'pointer'
                      }}
                    />
                  </div>
                </div>

                {/* 테두리 위치 */}
                <div className="info-field">
                  <label className="info-panel__label">테두리 위치</label>
                  <div className="border-position-controls">
                    <button
                      className={`border-position-btn ${(selectedObject.borderPosition || 'center') === 'outside' ? 'active' : ''}`}
                      onClick={() => {
                        const selectedObjs = getSelectedObjects();
                        if (selectedObjs.length > 0) {
                          selectedObjs.forEach(obj => {
                            updateObject(obj.id, (o) => ({ ...o, borderPosition: 'outside' }));
                          });
                        requestRender();
                          saveToHistory({ objects, zOrder, selectedId });
                        }
                      }}
                      title="외부 테두리"
                    >
                      <div className="border-position-preview outside">
                        <div className="preview-object"></div>
                        <div className="preview-border"></div>
                      </div>
                      <span>외부</span>
                    </button>
                    <button
                      className={`border-position-btn ${(selectedObject.borderPosition || 'center') === 'center' ? 'active' : ''}`}
                      onClick={() => {
                        const selectedObjs = getSelectedObjects();
                        if (selectedObjs.length > 0) {
                          selectedObjs.forEach(obj => {
                            updateObject(obj.id, (o) => ({ ...o, borderPosition: 'center' }));
                          });
                        requestRender();
                          saveToHistory({ objects, zOrder, selectedId });
                        }
                      }}
                      title="중앙 테두리"
                    >
                      <div className="border-position-preview center">
                        <div className="preview-object"></div>
                        <div className="preview-border"></div>
                      </div>
                      <span>중앙</span>
                    </button>
                    <button
                      className={`border-position-btn ${(selectedObject.borderPosition || 'center') === 'inside' ? 'active' : ''}`}
                      onClick={() => {
                        const selectedObjs = getSelectedObjects();
                        if (selectedObjs.length > 0) {
                          selectedObjs.forEach(obj => {
                            updateObject(obj.id, (o) => ({ ...o, borderPosition: 'inside' }));
                          });
                        requestRender();
                          saveToHistory({ objects, zOrder, selectedId });
                        }
                      }}
                      title="내부 테두리"
                    >
                      <div className="border-position-preview inside">
                        <div className="preview-object"></div>
                        <div className="preview-border"></div>
                      </div>
                      <span>내부</span>
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* 아웃글로우 */}
        {selectedId && selectedObject && (
        <div className="info-panel__section">
            <button 
              type="button" 
              className="info-panel__title info-panel__title--toggle" 
              onClick={() => setIsOutGlowOpen(prev => !prev)}
              aria-expanded={isOutGlowOpen} 
              aria-controls="right-panel-outglow"
            >
            <span className="info-panel__title-text">아웃글로우 효과</span>
            <span className="info-panel__title-arrow" aria-hidden="true"></span>
          </button>
            {isOutGlowOpen && (
          <div id="right-panel-outglow" className="info-panel__content">
                {/* 적용 여부 */}
                <div className="info-field">
                  <label className="info-panel__label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      type="checkbox"
                      checked={selectedObject.outGlowEnabled || false}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        const selectedObjs = getSelectedObjects();
                        
                        if (checked) {
                          // 아웃글로우 활성화: 기본값 설정
                          selectedObjs.forEach(obj => {
                            updateObject(obj.id, (o) => ({
                              ...o,
                              outGlowEnabled: true,
                              outGlowColor: o.outGlowColor || '#000000',
                              outGlowOpacity: o.outGlowOpacity ?? 100,
                              outGlowSize: o.outGlowSize ?? 10,
                              outGlowSpread: o.outGlowSpread ?? 0
                            }));
                          });
                        } else {
                          // 아웃글로우 비활성화
                          selectedObjs.forEach(obj => {
                            updateObject(obj.id, (o) => ({
                              ...o,
                              outGlowEnabled: false
                            }));
                          });
                        }
                        requestRender();
                        saveToHistory({ objects, zOrder, selectedId });
                      }}
                    />
                    <span>아웃글로우 적용</span>
                  </label>
                </div>

                {selectedObject.outGlowEnabled && (
                  <>
                    {/* 색상 */}
                    <div className="info-field">
                      <label className="info-panel__label">색상</label>
                      <div className="color-picker-container">
                        <div className="color-picker-always-visible">
                          <ChromePicker
                            color={selectedObject.outGlowColor || '#000000'}
                            onChange={(color) => {
                              const newColor = color.hex;
                              const selectedObjs = getSelectedObjects();
                              selectedObjs.forEach(obj => {
                                updateObject(obj.id, (o) => ({ ...o, outGlowColor: newColor }));
                              });
                              requestRender();
                            }}
                            onChangeComplete={() => {
                              const selectedObjs = getSelectedObjects();
                              if (selectedObjs.length > 0) {
                                saveToHistory({ objects, zOrder, selectedId });
                              }
                            }}
                            disableAlpha={true}
                          />
                        </div>
          </div>
        </div>

                    {/* 투명도 */}
                    <div className="info-field">
                      <label className="info-panel__label">투명도</label>
                      <input
                        type="number"
                        className="info-panel__input"
                        value={tempOutGlowOpacity !== '' ? tempOutGlowOpacity : String(selectedOutGlowOpacity)}
                        min="0"
                        max="100"
                        onFocus={(e) => {
                          focusedInputRef.current = 'outGlowOpacity';
                          e.target.select();
                        }}
                        onChange={(e) => setTempOutGlowOpacity(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.currentTarget.blur();
                          }
                        }}
                        onBlur={(e) => {
                          focusedInputRef.current = null;
                          const v = Math.max(0, Math.min(100, parseInt(e.target.value) || 0));
                          setTempOutGlowOpacity('');
                          const selectedObjs = getSelectedObjects();
                          selectedObjs.forEach(obj => {
                            updateObject(obj.id, (o) => ({ ...o, outGlowOpacity: v }));
                          });
                          requestRender();
                          saveToHistory({ objects, zOrder, selectedId });
                        }}
                      />
                      <span className="info-panel__hint">0-100%</span>
                    </div>

                    {/* 크기 */}
                    <div className="info-field">
                      <label className="info-panel__label">크기</label>
                      <input
                        type="number"
                        className="info-panel__input"
                        value={tempOutGlowSize !== '' ? tempOutGlowSize : String(selectedOutGlowSize)}
                        min="0"
                        max="200"
                        onFocus={(e) => {
                          focusedInputRef.current = 'outGlowSize';
                          e.target.select();
                        }}
                        onChange={(e) => setTempOutGlowSize(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.currentTarget.blur();
                          }
                        }}
                        onBlur={(e) => {
                          focusedInputRef.current = null;
                          const v = Math.max(0, Math.min(200, parseInt(e.target.value) || 0));
                          setTempOutGlowSize('');
                          const selectedObjs = getSelectedObjects();
                          selectedObjs.forEach(obj => {
                            updateObject(obj.id, (o) => ({ ...o, outGlowSize: v }));
                          });
                          requestRender();
                          saveToHistory({ objects, zOrder, selectedId });
                        }}
                      />
                      <span className="info-panel__hint">px</span>
                    </div>

                    {/* 스프레드 */}
                    <div className="info-field">
                      <label className="info-panel__label">스프레드</label>
                      <input
                        type="number"
                        className="info-panel__input"
                        value={tempOutGlowSpread !== '' ? tempOutGlowSpread : String(selectedOutGlowSpread)}
                        min="0"
                        max="100"
                        onFocus={(e) => {
                          focusedInputRef.current = 'outGlowSpread';
                          e.target.select();
                        }}
                        onChange={(e) => setTempOutGlowSpread(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.currentTarget.blur();
                          }
                        }}
                        onBlur={(e) => {
                          focusedInputRef.current = null;
                          const v = Math.max(0, Math.min(100, parseInt(e.target.value) || 0));
                          setTempOutGlowSpread('');
                          const selectedObjs = getSelectedObjects();
                          selectedObjs.forEach(obj => {
                            updateObject(obj.id, (o) => ({ ...o, outGlowSpread: v }));
                          });
                          requestRender();
                          saveToHistory({ objects, zOrder, selectedId });
                        }}
                      />
                      <span className="info-panel__hint">0-100%</span>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {/* 블러 효과 */}
        {selectedObject && selectedObject.type === 'image' && (
          <div className="info-panel__section">
            <button 
              type="button" 
              className="info-panel__title info-panel__title--toggle" 
              onClick={() => setIsBlurOpen(prev => !prev)}
              aria-expanded={isBlurOpen} 
              aria-controls="right-panel-blur"
            >
              <span className="info-panel__title-text">블러 효과</span>
              <span className="info-panel__title-arrow" aria-hidden="true"></span>
            </button>
            {isBlurOpen && selectedObject && (
              <div id="right-panel-blur" className="info-panel__content">
                {/* 적용 여부 */}
                <div className="info-field">
                  <label className="info-panel__label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      type="checkbox"
                      checked={selectedObject.blurEnabled || false}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        const selectedObjs = getSelectedObjects();
                        
                        if (checked) {
                          // 블러 활성화: 기본값 설정
                          selectedObjs.forEach(obj => {
                            updateObject(obj.id, (o) => ({
                              ...o,
                              blurEnabled: true,
                              blurRadius: o.blurRadius ?? 10
                            }));
                          });
                        } else {
                          // 블러 비활성화
                          selectedObjs.forEach(obj => {
                            updateObject(obj.id, (o) => ({
                              ...o,
                              blurEnabled: false
                            }));
                          });
                        }
                        requestRender();
                        saveToHistory({ objects, zOrder, selectedId });
                      }}
                    />
                    <span>블러 적용</span>
                  </label>
                </div>

                {selectedObject.blurEnabled && (
                  <>
                    {/* 블러 강도 */}
                    <div className="info-field">
                      <label className="info-panel__label">강도</label>
                      <input
                        type="number"
                        className="info-panel__input"
                        value={tempBlurRadius !== '' ? tempBlurRadius : String(selectedBlurRadius)}
                        min="0"
                        max="200"
                        onFocus={(e) => {
                          focusedInputRef.current = 'blurRadius';
                          e.target.select();
                        }}
                        onChange={(e) => setTempBlurRadius(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.currentTarget.blur();
                          }
                        }}
                        onBlur={(e) => {
                          focusedInputRef.current = null;
                          const v = Math.max(0, Math.min(200, parseInt(e.target.value) || 0));
                          setTempBlurRadius('');
                          const selectedObjs = getSelectedObjects();
                          selectedObjs.forEach(obj => {
                            updateObject(obj.id, (o) => ({ ...o, blurRadius: v }));
                          });
                          requestRender();
                          saveToHistory({ objects, zOrder, selectedId });
                        }}
                      />
                      <span className="info-panel__hint">px</span>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {/* 아트보드 정보 - 아트보드 선택 시에만 표시 */}
        {selectedArtboardId && !selectedId && (!selectedIds || selectedIds.size === 0) && (
          <div className="info-panel__section">
            <h3 className="info-panel__title">아트보드 정보</h3>
            <div className="info-panel__content">
              {(() => {
                const selectedArtboard = artboards.find(ab => ab.id === selectedArtboardId);
                if (!selectedArtboard) return null;
                
                return (
                  <>
                    <div className="info-panel__row">
                      <div className="info-field">
                        <label className="info-panel__label">이름</label>
                        <input
                          className="info-panel__input"
                          value={selectedArtboard.name}
                          onChange={(e) => {
                            const newName = e.target.value;
                            setArtboards(prev => prev.map(ab =>
                              ab.id === selectedArtboardId ? { ...ab, name: newName } : ab
                            ));
                            requestRender();
                          }}
                        />
                      </div>
                    </div>
                    <div className="info-panel__row--two">
                      <div className="info-field">
                        <label className="info-panel__label">X</label>
                        <input className="info-panel__input" type="number" value={selectedArtboard.x} disabled />
                      </div>
                      <div className="info-field">
                        <label className="info-panel__label">Y</label>
                        <input className="info-panel__input" type="number" value={selectedArtboard.y} disabled />
                      </div>
                    </div>
                    <div className="info-panel__row--two">
                      <div className="info-field">
                        <label className="info-panel__label">너비</label>
                        <input className="info-panel__input" type="number" value={selectedArtboard.width} disabled />
                      </div>
                      <div className="info-field">
                        <label className="info-panel__label">높이</label>
                        <input className="info-panel__input" type="number" value={selectedArtboard.height} disabled />
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        )}
      </aside>
      
      {/* 텍스트 입력 필드 */}
      {textInputMode && (
        <textarea
          ref={textInputRef}
          value={textInputValue}
          onChange={(e) => setTextInputValue(e.target.value)}
          onBlur={handleTextInputComplete}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && e.shiftKey) {
              // Shift+Enter: 입력 완료
              e.preventDefault();
              handleTextInputComplete();
            } else if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
              // Ctrl/Cmd+Enter: 입력 완료 (기존 기능 유지)
              e.preventDefault();
              handleTextInputComplete();
            } else if (e.key === 'Escape') {
              // Escape: 입력 취소
              e.preventDefault();
              handleTextInputCancel();
            }
            // 일반 Enter는 여러 줄 입력을 위해 그대로 두기
          }}
          style={{
            position: 'absolute',
            left: `${textInputMode.x}px`,
            top: `${textInputMode.y}px`,
            border: '2px solid #0ea5e9',
            borderRadius: '4px',
            padding: '8px',
            fontSize: '16px',
            fontFamily: 'PretendardJP',
            width: '300px',
            height: '100px',
            minWidth: '200px',
            minHeight: '50px',
            resize: 'both',
            zIndex: 10000,
            outline: 'none',
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
          }}
          placeholder="텍스트를 입력하세요..."
        />
      )}

      {/* 텍스트 교체 모달 */}
      {isTextReplaceOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 11000,
            backgroundColor: 'rgba(0,0,0,0.35)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          onClick={() => setIsTextReplaceOpen(false)}
        >
          <div
            style={{
              backgroundColor: '#ffffff',
              width: 560,
              maxHeight: '80vh',
              borderRadius: 8,
              boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ padding: '12px 16px', borderBottom: '1px solid #eee', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <strong>텍스트 일괄 변경</strong>
              <button 
                onClick={() => setIsTextReplaceOpen(false)} 
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 16 }}
              >✕</button>
            </div>
            <div style={{ padding: 16, overflow: 'auto' }}>
              {uniqueTexts.length === 0 ? (
                <div style={{ color: '#666' }}>캔버스에 변경할 텍스트가 없습니다.</div>
              ) : (
                uniqueTexts.map((t) => (
                  <div key={t} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 12 }}>
                    <label style={{ width: 220, fontSize: 12, color: '#555', lineHeight: '20px' }} title={t}>원문: {t}</label>
                    <textarea
                      rows={3}
                      value={textReplaceMap[t] ?? t}
                      onChange={(e) => setTextReplaceMap(prev => ({ ...prev, [t]: e.target.value }))}
                      style={{ flex: 1, padding: '6px 8px', border: '1px solid #ddd', borderRadius: 6, resize: 'vertical', fontFamily: 'inherit', fontSize: 14 }}
                      placeholder={'여러 줄 입력 가능 (줄바꿈: Enter)'}
                    />
                  </div>
                ))
              )}
            </div>
            <div style={{ padding: 16, borderTop: '1px solid #eee', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                onClick={() => setIsTextReplaceOpen(false)}
                style={{ padding: '8px 12px', background: '#f1f3f5', border: '1px solid #e9ecef', borderRadius: 6, cursor: 'pointer' }}
              >취소</button>
              <button
                onClick={applyTextReplace}
                style={{ padding: '8px 12px', background: '#007bff', color: '#fff', border: '1px solid #007bff', borderRadius: 6, cursor: 'pointer' }}
              >적용</button>
            </div>
          </div>
        </div>
      )}

      {/* 도움말 버튼 */}
      <button
        className="help-button"
        onClick={() => setIsHelpModalOpen(true)}
        title="도움말"
      >
        ?
      </button>

      {/* 도움말 모달 */}
      <HelpModal
        isOpen={isHelpModalOpen}
        onClose={() => setIsHelpModalOpen(false)}
      />

      {/* 템플릿 갤러리 모달 */}
      <TemplateGallery
        isOpen={isTemplateGalleryOpen}
        onClose={() => setIsTemplateGalleryOpen(false)}
        onSelectTemplate={handleSelectTemplateFromGallery}
      />
    </div>
  );
};


